import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  AtSign,
  Camera,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Clock,
  Download,
  FileText,
  Forward,
  Hash,
  Info,
  ListTodo,
  Loader2,
  Lock,
  LogOut,
  MessageCircle,
  MessageSquarePlus,
  MessagesSquare,
  Mic,
  MoreVertical,
  Paperclip,
  Pencil,
  Plus,
  Reply,
  Search,
  Send,
  ShieldCheck,
  ShieldOff,
  Smile,
  SmilePlus,
  Sticker,
  Ticket,
  UserMinus,
  UserPlus,
  Users,
  Video,
  X,
} from 'lucide-react';
import {
  Fragment,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError, api, type Attachment } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n, type Locale, type TranslationKey } from '../lib/i18n';
import {
  activeTrigger,
  deleteWhole,
  followEdit,
  insertMention,
  splitMentions,
  trimWithMentions,
  type Trigger,
} from '../lib/mentions';
import { DAY_NAMES, formatDate, formatDateTime, formatNumber, formatShortTime } from '../lib/format';
import {
  MAX_FORWARD_TO,
  QUICK_REACTIONS,
  catchUp,
  chatKeys,
  deliver,
  discard,
  draftMentions,
  drafts,
  enqueue,
  forwardMessage,
  isGroupChat,
  loadOlder,
  loadThread,
  markRead,
  newClientId,
  quoteOf,
  reactTo,
  recentStickers,
  referencePath,
  refreshGroup,
  rememberSticker,
  useChatEvents,
  useEnterSends,
  useInvitations,
  usePageInView,
  useReferences,
  useStickerTray,
  type ChatDetail,
  type ChatEvent,
  type ChatGroupInvitee,
  type ChatGroupMember,
  type ChatInvitation,
  type ChatList,
  type ChatMention,
  type ChatMessage,
  type ChatName,
  type ChatPerson,
  type ChatPersonOption,
  type ChatPreview,
  type ChatQuote,
  type ChatReaction,
  type ChatReference,
  type ChatSticker,
  type ChatStickerPack,
  type ChatSummary,
  type Outgoing,
  type ThreadState,
} from '../lib/chat';
import { onlyEmoji } from '../lib/emoji';
import { fold } from '../components/PeoplePicker';
import {
  AttachmentList,
  DropZone,
  PendingTray,
  VoiceNotePlayer,
  VoiceRecorder,
  useUploads,
  voiceNoteName,
  type Uploads,
} from '../components/Attachments';
import { EmojiPicker } from '../components/EmojiPicker';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  LoadingBlock,
  Modal,
  Spinner,
  useToast,
} from '../components/ui';

/**
 * Chat (operator, 2026-10-19: "i want to build chating system under email when
 * click on this tab go to full page with windows like whatsapp web can chose
 * chat or new chat only with employees it's also internal i can send text audio
 * or images and emojj"; then "no roles it's like email system for every
 * employee").
 *
 * Routes `/chat` and `/chat/:id`: the whole page, in two panes as WhatsApp Web
 * lays them out — the chats on one side (or New chat, with every employee), the
 * open chat on the other. On a phone, one pane at a time. Words with emoji,
 * pictures and files, and voice notes; everything stays inside the system.
 *
 * Groups (operator, 2026-10-22: "anyone can create group and add employees to
 * this group when someone add another to group he must accept not add auto no
 * auto option"): New group, from New chat or the ⋮ menu, picks the people to
 * invite and names it. The invitations wait on top of the chats with Accept and
 * Decline — nobody is put in a group. A group's info, from its header, lists its
 * members and those invited; anyone invites more, its admins rename it, take
 * people out and make others admins, and anyone leaves.
 */

/** A group's name, at most — the API's MAX_GROUP_NAME. */
const MAX_GROUP_NAME = 60;

const isPicture = (mime: string) => /^image\/(png|jpeg|gif|webp)$/.test(mime);

/** Two letters for someone's circle: their initials, in whichever script their name is in. */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => Array.from(part)[0]?.toUpperCase() ?? '')
    .join('');
}

/** How many days ago a moment was, by the calendar here — not by 24-hour stretches. */
function daysAgo(d: Date): number {
  const then = new Date(d);
  then.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((today.getTime() - then.getTime()) / 86_400_000);
}

const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

/** "Today", "Yesterday", the weekday within the week, else the date — over a day's messages. */
function dayLabel(d: Date, locale: Locale, t: (key: TranslationKey) => string): string {
  const n = daysAgo(d);
  if (n === 0) return t('chat.today');
  if (n === 1) return t('chat.yesterday');
  if (n > 1 && n < 7) return DAY_NAMES[locale][d.getDay()];
  return formatDate(d, locale);
}

/** The time today, else the day — beside a chat in the list. */
function listTime(iso: string, locale: Locale, t: (key: TranslationKey) => string): string {
  const d = new Date(iso);
  return daysAgo(d) === 0 ? formatShortTime(d, locale) : dayLabel(d, locale, t);
}

type Translate = (key: TranslationKey, vars?: Record<string, string | number>) => string;
type Localize = (row: { fullName?: string; fullNameAr?: string | null; name?: string | null; nameAr?: string | null } | null | undefined) => string;

/** What a chat is called: a group's name, or the other person's. */
function chatTitle(chat: ChatSummary | undefined, localized: Localize): string {
  if (!chat) return '';
  return isGroupChat(chat) ? (chat.group?.name ?? '') : localized(chat.other);
}

/**
 * The tones a group's names are drawn in over their messages, as WhatsApp gives
 * each person a colour: the brand's own and the console's accents, each pair
 * readable as text in both themes. One person keeps theirs in every group.
 */
const SENDER_TONES = [
  'text-primary-600 dark:text-primary-400',
  'text-ai-circuit-text dark:text-ai-circuit',
  'text-ai-plasma-text dark:text-ai-plasma-lift',
  'text-ai-ember-text dark:text-ai-ember',
  'text-success-fg dark:text-success-dark',
  'text-warning-fg dark:text-warning-dark',
];

function senderTone(id: string): string {
  let hash = 0;
  for (const ch of id) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  return SENDER_TONES[hash % SENDER_TONES.length];
}

/**
 * A group's own line in words — "Mona joined — invited by Omar", "You left" —
 * between its messages, and as its list row's preview. Someone deleted since is
 * "a former member".
 */
function eventText(
  m: { event?: ChatEvent | null; sender?: ChatName; senderId?: string; eventUser?: ChatName | null; body: string | null },
  me: string,
  t: Translate,
  localized: Localize,
): string {
  const senderId = m.sender?.id ?? m.senderId;
  const mine = senderId === me;
  const who = m.sender ? localized(m.sender) : t('chat.group.someone');
  const other = m.eventUser ? localized(m.eventUser) : t('chat.group.someone');
  const name = m.body ?? '';
  switch (m.event) {
    case 'GROUP_CREATED':
      return mine ? t('chat.event.createdYou', { name }) : t('chat.event.created', { who, name });
    case 'GROUP_RENAMED':
      return mine ? t('chat.event.renamedYou', { name }) : t('chat.event.renamed', { who, name });
    case 'MEMBER_JOINED':
      if (!m.eventUser) return mine ? t('chat.event.joinedYouAlone') : t('chat.event.joinedAlone', { who });
      if (m.eventUser.id === me) return t('chat.event.joinedByYou', { who });
      return mine ? t('chat.event.joinedYou', { by: other }) : t('chat.event.joined', { who, by: other });
    case 'MEMBER_LEFT':
      return mine ? t('chat.event.leftYou') : t('chat.event.left', { who });
    case 'MEMBER_REMOVED':
      if (mine) return t('chat.event.removedYou', { whom: other });
      if (m.eventUser?.id === me) return t('chat.event.removedYouBy', { who });
      return t('chat.event.removed', { who, whom: other });
    default:
      return '';
  }
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

export function ChatPage() {
  const { id } = useParams();
  const { t } = useI18n();
  const [panel, setPanel] = useState<'chats' | 'new' | 'group'>('chats');
  useChatEvents(id ?? null);

  return (
    <div className="flex min-h-0 flex-1 overflow-hidden bg-[var(--surface-card)]">
      <aside
        aria-label={t('chat.title')}
        className={clsx(
          'min-h-0 w-full flex-col border-e hairline md:flex md:w-[340px] md:shrink-0 lg:w-[360px] xl:w-[400px]',
          id ? 'hidden' : 'flex',
        )}
      >
        {panel === 'new' ? (
          <NewChatPanel onBack={() => setPanel('chats')} onNewGroup={() => setPanel('group')} />
        ) : panel === 'group' ? (
          <NewGroupPanel onBack={() => setPanel('chats')} />
        ) : (
          <ChatsPanel activeId={id ?? null} onNewChat={() => setPanel('new')} onNewGroup={() => setPanel('group')} />
        )}
      </aside>
      <section className={clsx('min-w-0 flex-1 flex-col', id ? 'flex' : 'hidden md:flex')}>
        {id ? <ChatWindow key={id} id={id} /> : <ChatIntro onNewChat={() => setPanel('new')} />}
      </section>
    </div>
  );
}

/** A group's circle: no picture of its own, so the people sign, in the brand's tone as a person's initials are. */
function GroupAvatar({ size = 'lg' }: { size?: 'md' | 'lg' | 'xl' }) {
  return (
    <span
      aria-hidden
      className={clsx(
        'grid shrink-0 place-items-center rounded-full bg-primary/12 text-primary-600 dark:text-primary-400',
        size === 'xl' ? 'h-24 w-24' : size === 'lg' ? 'h-12 w-12' : 'h-10 w-10',
      )}
    >
      <Users className={size === 'xl' ? 'h-11 w-11' : size === 'lg' ? 'h-6 w-6' : 'h-5 w-5'} />
    </span>
  );
}

/** A chat's circle: the group's, or the other person's. */
function ChatCircle({ chat, size = 'lg' }: { chat: ChatSummary; size?: 'md' | 'lg' }) {
  return isGroupChat(chat) || !chat.other ? <GroupAvatar size={size} /> : <ChatAvatar person={chat.other} size={size} />;
}

function ChatAvatar({
  person,
  size = 'lg',
}: {
  person: Pick<ChatPerson, 'fullName'> & { fullNameAr?: string | null; isActive?: boolean; online?: boolean };
  size?: 'md' | 'lg';
}) {
  const { t, localized } = useI18n();
  return (
    <span className="relative shrink-0">
      <span
        aria-hidden
        className={clsx(
          'grid place-items-center rounded-full font-semibold',
          size === 'lg' ? 'h-12 w-12 text-body' : 'h-10 w-10 text-body-sm',
          // The brand badge's tones, which read in both themes; someone who left goes grey.
          person.isActive === false
            ? 'bg-[var(--surface-sunken)] text-[var(--text-tertiary)]'
            : 'bg-primary/12 text-primary-600 dark:text-primary-400',
        )}
      >
        {initials(localized(person))}
      </span>
      {/* Online: they have the system open right now. */}
      {person.online && (
        <span
          className="absolute bottom-0 end-0 h-3 w-3 rounded-full bg-status-online ring-2 ring-[var(--surface-card)]"
          title={t('chat.online')}
        >
          <span className="sr-only">{t('chat.online')}</span>
        </span>
      )}
    </span>
  );
}

/** The chats' ⋮ menu, as WhatsApp Web's: New group first, then the chats' own settings — for now, what Enter does. */
function ChatSettings({ onNewGroup }: { onNewGroup: () => void }) {
  const { t } = useI18n();
  const [enterSends, setEnterSends] = useEnterSends();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={box} className="relative">
      <button
        ref={button}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('chat.settings')}
        title={t('chat.settings')}
        className={clsx(
          'grid h-10 w-10 place-items-center rounded-full text-[var(--text-secondary)] transition-colors duration-fast',
          'hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]',
          open && 'bg-[var(--surface-sunken)] text-[var(--text-primary)]',
        )}
      >
        <MoreVertical className="h-5 w-5" aria-hidden />
      </button>
      {open && (
        <div role="menu" className="absolute end-0 top-full z-dropdown mt-1 w-72 rounded-md surface p-1.5 shadow-lg">
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onNewGroup();
            }}
            className="flex w-full items-center gap-3 rounded-sm px-2.5 py-2 text-start text-body-sm font-medium text-[var(--text-primary)] transition-colors duration-fast hover:bg-[var(--surface-sunken)]"
          >
            <Users className="h-4 w-4 shrink-0 text-[var(--text-secondary)]" aria-hidden />
            {t('chat.newGroup')}
          </button>
          <div className="my-1 border-t hairline" role="separator" />
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={enterSends}
            onClick={() => setEnterSends(!enterSends)}
            className="flex w-full items-start gap-3 rounded-sm px-2.5 py-2 text-start transition-colors duration-fast hover:bg-[var(--surface-sunken)]"
          >
            <span
              aria-hidden
              className={clsx(
                'mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-xs border',
                enterSends ? 'border-primary bg-primary text-white' : 'hairline',
              )}
            >
              {enterSends && <Check className="h-3 w-3" />}
            </span>
            <span className="min-w-0">
              <span className="block text-body-sm font-medium text-[var(--text-primary)]">{t('chat.enterSends')}</span>
              <span className="mt-0.5 block text-caption text-muted">
                {enterSends ? t('chat.enterSendsOn') : t('chat.enterSendsOff')}
              </span>
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

function IconButton({
  label,
  onClick,
  pressed,
  keepFocus = false,
  children,
  className,
}: {
  label: string;
  onClick: () => void;
  pressed?: boolean;
  /** Pressed, it leaves the focus — and the cursor — where they were: the message box. */
  keepFocus?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseDown={keepFocus ? (e) => e.preventDefault() : undefined}
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      className={clsx(
        'grid h-10 w-10 shrink-0 place-items-center rounded-full text-[var(--text-secondary)] transition-colors duration-fast',
        'hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]',
        pressed && 'bg-[var(--surface-sunken)] text-[var(--text-primary)]',
        className,
      )}
    >
      {children}
    </button>
  );
}

function PanelHeader({ title, start, action }: { title: ReactNode; start?: ReactNode; action?: ReactNode }) {
  return (
    <header className="flex h-16 shrink-0 items-center gap-2 border-b hairline px-3 sm:px-4">
      {start}
      <h1 className="min-w-0 flex-1 truncate text-h3 text-[var(--text-primary)]">{title}</h1>
      {action}
    </header>
  );
}

function SearchBox({
  value,
  onChange,
  placeholder,
  autoFocus,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  autoFocus?: boolean;
}) {
  return (
    <div className="relative">
      <Search
        className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-tertiary)]"
        aria-hidden
      />
      <input
        type="search"
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="h-9 w-full rounded-md border hairline bg-[var(--surface-sunken)] pe-3 ps-9 text-body-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:border-primary dark:placeholder:text-[var(--text-secondary)]"
      />
    </div>
  );
}

/** Opens the caller's chat with someone — theirs already, or a new one — and goes to it. */
function useOpenChat(after?: () => void) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useI18n();
  return useMutation({
    mutationFn: (userId: string) => api.post<ChatSummary>('/chat/conversations', { userId }),
    onSuccess: (chat) => {
      qc.setQueryData(chatKeys.one(chat.id), chat);
      after?.();
      navigate(`/chat/${chat.id}`);
    },
    onError: (e: Error) => toast.error(e.message || t('chat.openFailed')),
  });
}

/** Whether a person, or a chat's other person, answers to every word typed — by name in either language, job or department. */
function useMatcher(query: string) {
  const words = fold(query).split(/\s+/).filter(Boolean);
  return (texts: Array<string | null | undefined>) => {
    if (words.length === 0) return true;
    const haystack = fold(texts.join(' '));
    return words.every((word) => haystack.includes(word));
  };
}

// ---------------------------------------------------------------------------
// The chats
// ---------------------------------------------------------------------------

function ChatsPanel({
  activeId,
  onNewChat,
  onNewGroup,
}: {
  activeId: string | null;
  onNewChat: () => void;
  onNewGroup: () => void;
}) {
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'unread' | 'groups'>('all');
  const searching = query.trim().length > 0;
  const matches = useMatcher(query);
  const open = useOpenChat();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: chatKeys.list,
    queryFn: () => api.get<ChatList>('/chat/conversations'),
    // Who is online moves without a message: asked again every half minute.
    refetchInterval: 30_000,
  });
  const invitations = useInvitations();
  // Searching finds people as well as chats: anyone without a chat yet starts one from here.
  const people = useQuery({
    queryKey: chatKeys.people,
    queryFn: () => api.get<ChatPersonOption[]>('/chat/people'),
    enabled: searching,
    staleTime: 30_000,
  });

  const all = data?.items ?? [];
  const unreadChats = all.filter((c) => c.unread > 0).length;
  const chats = all
    .filter((c) => filter === 'all' || (filter === 'unread' ? c.unread > 0 : isGroupChat(c)))
    .filter((c) =>
      isGroupChat(c) ? matches([c.group?.name]) : matches([c.other?.fullName, c.other?.fullNameAr, c.other?.jobTitle]),
    );
  const invites = (invitations.data ?? []).filter((i) => matches([i.group.name]));
  const withChat = new Set(all.flatMap((c) => (c.other ? [c.other.id] : [])));
  const others = searching
    ? (people.data ?? [])
        .filter((p) => !withChat.has(p.id))
        .filter((p) => matches([p.fullName, p.fullNameAr, p.jobTitle, ...p.departments.flatMap((d) => [d.name, d.nameAr])]))
        .slice(0, 30)
    : [];

  return (
    <>
      <PanelHeader
        title={t('chat.title')}
        action={
          <>
            <IconButton label={t('chat.new')} onClick={onNewChat}>
              <MessageSquarePlus className="h-5 w-5" aria-hidden />
            </IconButton>
            <ChatSettings onNewGroup={onNewGroup} />
          </>
        }
      />
      <div className="shrink-0 space-y-2.5 border-b hairline px-3 py-2.5">
        <SearchBox value={query} onChange={setQuery} placeholder={t('chat.search')} />
        <div className="flex gap-1.5" role="group" aria-label={t('chat.filter.label')}>
          {(['all', 'unread', 'groups'] as const).map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={filter === f}
              onClick={() => setFilter(f)}
              className={clsx(
                'rounded-pill px-3 py-1 text-body-sm font-medium transition-colors duration-fast',
                filter === f
                  ? 'bg-primary/12 text-primary-600 dark:text-primary-400'
                  : 'bg-[var(--surface-sunken)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
              )}
            >
              {t(f === 'all' ? 'chat.filter.all' : f === 'unread' ? 'chat.filter.unread' : 'chat.filter.groups')}
              {f === 'unread' && unreadChats > 0 && <span className="ms-1.5 tabular">{unreadChats}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : (
          <>
            {invites.length > 0 && filter !== 'unread' && <InvitationList invitations={invites} />}
            {chats.length > 0 && (
              <ul>
                {chats.map((chat) => (
                  <ChatRow key={chat.id} chat={chat} active={chat.id === activeId} />
                ))}
              </ul>
            )}
            {others.length > 0 && (
              <>
                <p className="px-4 pb-1.5 pt-4 text-overline uppercase text-[var(--text-tertiary)]">{t('chat.people')}</p>
                <ul>
                  {others.map((person) => (
                    <PersonRow
                      key={person.id}
                      person={person}
                      busy={open.isPending && open.variables === person.id}
                      onPick={() => open.mutate(person.id)}
                    />
                  ))}
                </ul>
              </>
            )}
            {chats.length === 0 &&
              others.length === 0 &&
              (searching ? (
                people.isLoading ? (
                  <LoadingBlock label={t('chat.peopleLoading')} />
                ) : invites.length === 0 ? (
                  <p className="px-6 py-10 text-center text-body-sm text-muted">{t('chat.noMatch')}</p>
                ) : null
              ) : filter === 'unread' && all.length > 0 ? (
                <EmptyState title={t('chat.empty.unread')} hint={t('chat.empty.unreadHint')} icon={<CheckCheck className="h-8 w-8" />} />
              ) : filter === 'groups' ? (
                invites.length === 0 && (
                  <EmptyState
                    title={t('chat.empty.groups')}
                    hint={t('chat.empty.groupsHint')}
                    icon={<Users className="h-8 w-8" />}
                    action={
                      <Button variant="primary" icon={<Users className="h-4 w-4" />} onClick={onNewGroup}>
                        {t('chat.newGroup')}
                      </Button>
                    }
                  />
                )
              ) : invites.length > 0 ? null : (
                <EmptyState
                  title={t('chat.empty.title')}
                  hint={t('chat.empty.hint')}
                  icon={<MessagesSquare className="h-8 w-8" />}
                  action={
                    <Button variant="primary" icon={<MessageSquarePlus className="h-4 w-4" />} onClick={onNewChat}>
                      {t('chat.new')}
                    </Button>
                  }
                />
              ))}
          </>
        )}
      </div>
    </>
  );
}

function ChatRow({ chat, active }: { chat: ChatSummary; active: boolean }) {
  const { t, locale, localized } = useI18n();
  const unread = chat.unread > 0;
  return (
    <li>
      <Link
        to={`/chat/${chat.id}`}
        aria-current={active ? 'page' : undefined}
        className={clsx(
          'flex items-center gap-3 px-3 py-2.5 transition-colors duration-fast sm:px-4',
          active ? 'bg-[var(--surface-sunken)]' : 'hover:bg-[var(--surface-sunken)]',
        )}
      >
        <ChatCircle chat={chat} />
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-2">
            <span
              className={clsx('min-w-0 flex-1 truncate text-body text-[var(--text-primary)]', unread ? 'font-semibold' : 'font-medium')}
            >
              <bdi>{chatTitle(chat, localized)}</bdi>
            </span>
            {chat.lastMessageAt && (
              <span
                className={clsx(
                  'shrink-0 text-caption tabular',
                  unread ? 'font-semibold text-primary-600 dark:text-primary-400' : 'text-muted',
                )}
                title={formatDateTime(chat.lastMessageAt, locale)}
              >
                {listTime(chat.lastMessageAt, locale, t)}
              </span>
            )}
          </span>
          <span className="mt-0.5 flex items-center gap-2">
            <PreviewLine chat={chat} />
            {/* Mentioned in something not read yet: "@" beside the count, as WhatsApp marks it. */}
            {unread && (chat.unreadMentions ?? 0) > 0 && (
              <span
                className="ms-auto grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary text-white"
                title={t('chat.mention.unread')}
              >
                <span className="sr-only">{t('chat.mention.unread')}</span>
                <AtSign className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
              </span>
            )}
            {unread && (
              <span
                className={clsx(
                  'grid h-5 min-w-5 shrink-0 place-items-center rounded-pill bg-primary px-1.5 text-[11px] font-bold text-white tabular',
                  !(chat.unreadMentions ?? 0) && 'ms-auto',
                )}
              >
                <span className="sr-only">{t('chat.unreadCount', { n: formatNumber(chat.unread, locale) })}</span>
                <span aria-hidden>{chat.unread > 99 ? '99+' : chat.unread}</span>
              </span>
            )}
          </span>
        </span>
      </Link>
    </li>
  );
}

/** What a message without words — or beside its words — carries, as the list says it. A sticker says so. */
function carriedLabel(
  m: Pick<ChatPreview, 'pictures' | 'voiceNotes' | 'videos' | 'files' | 'sticker'>,
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string,
) {
  if (m.sticker) return { icon: <Sticker className="h-4 w-4 shrink-0" aria-hidden />, label: t('chat.preview.sticker') };
  const total = m.pictures + m.voiceNotes + m.videos + m.files;
  if (total === 0) return { icon: null, label: '' };
  if (m.voiceNotes === total) return { icon: <Mic className="h-4 w-4 shrink-0" aria-hidden />, label: t('chat.preview.voice') };
  if (m.pictures === total) {
    return {
      icon: <Camera className="h-4 w-4 shrink-0" aria-hidden />,
      label: total === 1 ? t('chat.preview.photo') : t('chat.preview.photos', { n: total }),
    };
  }
  if (m.videos === total) return { icon: <Video className="h-4 w-4 shrink-0" aria-hidden />, label: t('chat.preview.video') };
  if (m.files === total) {
    return {
      icon: <FileText className="h-4 w-4 shrink-0" aria-hidden />,
      label: total === 1 ? t('chat.preview.file') : t('chat.preview.files', { n: total }),
    };
  }
  return { icon: <Paperclip className="h-4 w-4 shrink-0" aria-hidden />, label: t('chat.preview.attachments', { n: total }) };
}

function PreviewLine({ chat }: { chat: ChatSummary }) {
  const { t, localized } = useI18n();
  const { user } = useAuth();
  const m = chat.lastMessage;
  if (!m) return <span className="min-w-0 flex-1 truncate text-body-sm text-muted">{t('chat.noMessages')}</span>;
  // A group's own line says what happened, in italics as WhatsApp words one.
  if (m.event) {
    return (
      <span className="min-w-0 flex-1 truncate text-body-sm italic text-muted">
        <bdi>{eventText(m, user?.id ?? '', t, localized)}</bdi>
      </span>
    );
  }
  const read = m.mine && chat.reads.theirs >= m.seq;
  const { icon, label } = carriedLabel(m, t);
  // In a group, who wrote it comes first — "Mona: …"; one's own is ticked instead.
  const by = isGroupChat(chat) && !m.mine && m.sender ? localized(m.sender) : null;
  return (
    <span className="flex min-w-0 flex-1 items-center gap-1 text-body-sm text-muted">
      {m.mine &&
        (read ? (
          <CheckCheck className="h-4 w-4 shrink-0 text-info-fg dark:text-info-dark" aria-label={t('chat.read')} />
        ) : (
          <Check className="h-4 w-4 shrink-0" aria-label={t('chat.sent')} />
        ))}
      {by && (
        <span className="max-w-[45%] shrink-0 truncate font-medium text-[var(--text-secondary)]">
          <bdi>{by}</bdi>:
        </span>
      )}
      {icon}
      <span className="min-w-0 truncate" dir="auto">
        {m.body ?? label}
      </span>
    </span>
  );
}

/**
 * The invitations waiting for the caller's answer, on top of the chats: each
 * group, who invited them and who is in it, with Accept and Decline — nobody is
 * put in a group, so this is how one joins.
 */
function InvitationList({ invitations }: { invitations: ChatInvitation[] }) {
  const { t } = useI18n();
  return (
    <section aria-label={t('chat.invites.title')} className="border-b hairline pb-1">
      <p className="px-4 pb-1.5 pt-3 text-overline uppercase text-[var(--text-tertiary)]">{t('chat.invites.title')}</p>
      <ul>
        {invitations.map((invitation) => (
          <InvitationRow key={invitation.id} invitation={invitation} />
        ))}
      </ul>
      <p className="px-4 pb-2 pt-1 text-caption text-faint">{t('chat.invites.hint')}</p>
    </section>
  );
}

function InvitationRow({ invitation }: { invitation: ChatInvitation }) {
  const { t, locale, localized } = useI18n();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const { group } = invitation;
  const accept = useMutation({
    mutationFn: () => api.post<ChatDetail>(`/chat/invitations/${invitation.id}/accept`),
    onSuccess: (chat) => {
      qc.setQueryData(chatKeys.one(chat.id), chat);
      // Open on its own page already, its messages were refused until now: they are read afresh.
      void qc.resetQueries({ queryKey: chatKeys.thread(chat.id) });
      refreshGroup(qc, chat.id);
      toast.success(t('chat.invites.accepted', { name: group.name }));
      navigate(`/chat/${chat.id}`);
    },
    onError: (e: Error) => {
      toast.error(e.message);
      refreshGroup(qc);
    },
  });
  const decline = useMutation({
    mutationFn: () => api.post(`/chat/invitations/${invitation.id}/decline`),
    onSuccess: () => {
      refreshGroup(qc);
      toast.success(t('chat.invites.declined'));
    },
    onError: (e: Error) => {
      toast.error(e.message);
      refreshGroup(qc);
    },
  });
  const busy = accept.isPending || decline.isPending;
  const people = group.people.map((p) => localized(p)).join(locale === 'ar' ? '، ' : ', ');
  return (
    <li className="flex items-start gap-3 px-3 py-2.5 sm:px-4">
      <GroupAvatar />
      <div className="min-w-0 flex-1">
        <p className="truncate text-body font-semibold text-[var(--text-primary)]">
          <bdi>{group.name}</bdi>
        </p>
        <p className="truncate text-body-sm text-muted">
          <bdi>{t('chat.invites.from', { name: localized(invitation.invitedBy) })}</bdi>
          <span aria-hidden> · </span>
          {group.members === 1 ? t('chat.group.memberOne') : t('chat.group.membersCount', { n: formatNumber(group.members, locale) })}
        </p>
        {people && (
          <p className="truncate text-caption text-faint">
            <bdi>{people}</bdi>
          </p>
        )}
        <div className="mt-2 flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="primary"
            icon={<Check className="h-4 w-4" />}
            loading={accept.isPending}
            disabled={busy}
            onClick={() => accept.mutate()}
            aria-label={t('chat.invites.acceptLabel', { name: group.name })}
          >
            {t('chat.invites.accept')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            icon={<X className="h-4 w-4" />}
            loading={decline.isPending}
            disabled={busy}
            onClick={() => decline.mutate()}
            aria-label={t('chat.invites.declineLabel', { name: group.name })}
          >
            {t('chat.invites.decline')}
          </Button>
        </div>
      </div>
    </li>
  );
}

function PersonRow({ person, onPick, busy }: { person: ChatPersonOption; onPick: () => void; busy: boolean }) {
  const { localized } = useI18n();
  const where = [person.jobTitle, ...person.departments.map((d) => localized(d))].filter(Boolean).join(' · ');
  return (
    <li>
      <button
        type="button"
        onClick={onPick}
        disabled={busy}
        className="flex w-full items-center gap-3 px-3 py-2.5 text-start transition-colors duration-fast hover:bg-[var(--surface-sunken)] disabled:opacity-60 sm:px-4"
      >
        <ChatAvatar person={person} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-body font-medium text-[var(--text-primary)]">
            <bdi>{localized(person)}</bdi>
          </span>
          {where && (
            <span className="block truncate text-body-sm text-muted">
              <bdi>{where}</bdi>
            </span>
          )}
        </span>
        {busy && <Spinner className="h-4 w-4" />}
      </button>
    </li>
  );
}

/**
 * New chat: every employee but you, whatever their department — as mail. Picking
 * one opens your chat with them. New group, on top, as WhatsApp Web puts it.
 */
function NewChatPanel({ onBack, onNewGroup }: { onBack: () => void; onNewGroup: () => void }) {
  const { t, locale, localized } = useI18n();
  const [query, setQuery] = useState('');
  const matches = useMatcher(query);
  const open = useOpenChat(onBack);
  const people = useQuery({
    queryKey: chatKeys.people,
    queryFn: () => api.get<ChatPersonOption[]>('/chat/people'),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
  const shown = (people.data ?? [])
    .filter((p) => matches([p.fullName, p.fullNameAr, p.jobTitle, ...p.departments.flatMap((d) => [d.name, d.nameAr])]))
    .sort((a, b) => localized(a).localeCompare(localized(b), locale));

  return (
    <div
      className="flex min-h-0 flex-1 flex-col"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onBack();
      }}
    >
      <PanelHeader
        title={t('chat.new')}
        start={
          <IconButton label={t('chat.back')} onClick={onBack}>
            <ArrowLeft className="h-5 w-5 flip-rtl" aria-hidden />
          </IconButton>
        }
      />
      <div className="shrink-0 border-b hairline px-3 py-2.5">
        <SearchBox value={query} onChange={setQuery} placeholder={t('chat.searchPeople')} autoFocus />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {!query.trim() && (
          <button
            type="button"
            onClick={onNewGroup}
            className="flex w-full items-center gap-3 px-3 py-2.5 text-start transition-colors duration-fast hover:bg-[var(--surface-sunken)] sm:px-4"
          >
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-primary text-white" aria-hidden>
              <Users className="h-6 w-6" />
            </span>
            <span className="text-body font-medium text-[var(--text-primary)]">{t('chat.newGroup')}</span>
          </button>
        )}
        {people.isLoading ? (
          <LoadingBlock label={t('chat.peopleLoading')} />
        ) : people.error ? (
          <ErrorState error={people.error} onRetry={() => void people.refetch()} />
        ) : shown.length === 0 ? (
          <p className="px-6 py-10 text-center text-body-sm text-muted">{t('chat.noPeople')}</p>
        ) : (
          <>
            <p className="px-4 pb-1.5 pt-3 text-overline uppercase text-[var(--text-tertiary)]">{t('chat.everyone')}</p>
            <ul>
              {shown.map((person) => (
                <PersonRow
                  key={person.id}
                  person={person}
                  busy={open.isPending && open.variables === person.id}
                  onPick={() => open.mutate(person.id)}
                />
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

/** A person to tick in a list of people — inviting to a group, or forwarding. */
function TickRow({
  person,
  picked,
  disabled = false,
  onToggle,
  note,
}: {
  person: ChatPersonOption | ChatPerson;
  picked: boolean;
  disabled?: boolean;
  onToggle: () => void;
  /** Under the name: a job title, departments. */
  note?: string | null;
}) {
  const { localized } = useI18n();
  return (
    <li>
      <button
        type="button"
        role="checkbox"
        aria-checked={picked}
        disabled={disabled}
        onClick={onToggle}
        className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-start transition-colors duration-fast hover:bg-[var(--surface-sunken)] disabled:cursor-not-allowed disabled:opacity-50 sm:px-4"
      >
        <span
          aria-hidden
          className={clsx(
            'grid h-5 w-5 shrink-0 place-items-center rounded-full border',
            picked ? 'border-primary bg-primary text-white' : 'hairline',
          )}
        >
          {picked && <Check className="h-3.5 w-3.5" />}
        </span>
        <ChatAvatar person={person} size="md" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-body-sm font-medium text-[var(--text-primary)]">
            <bdi>{localized(person)}</bdi>
          </span>
          {note && (
            <span className="block truncate text-caption text-muted">
              <bdi>{note}</bdi>
            </span>
          )}
        </span>
      </button>
    </li>
  );
}

/**
 * New group, in two steps as WhatsApp Web's: the people to invite — any active
 * employee, none at all is fine — then its name. Each person chosen gets an
 * invitation and joins only by accepting; the group opens with its starter in it,
 * as its admin.
 */
function NewGroupPanel({ onBack }: { onBack: () => void }) {
  const { t, locale, localized } = useI18n();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const [step, setStep] = useState<'people' | 'name'>('people');
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState<ChatPersonOption[]>([]);
  const [name, setName] = useState('');
  const matches = useMatcher(query);
  const people = useQuery({
    queryKey: chatKeys.people,
    queryFn: () => api.get<ChatPersonOption[]>('/chat/people'),
    staleTime: 30_000,
  });
  const picked = new Set(chosen.map((p) => p.id));
  const shown = (people.data ?? [])
    .filter((p) => matches([p.fullName, p.fullNameAr, p.jobTitle, ...p.departments.flatMap((d) => [d.name, d.nameAr])]))
    .sort((a, b) => localized(a).localeCompare(localized(b), locale));
  const toggle = (person: ChatPersonOption) =>
    setChosen((list) => (list.some((p) => p.id === person.id) ? list.filter((p) => p.id !== person.id) : [...list, person]));

  const words = name.trim();
  const length = Array.from(words).length;
  const create = useMutation({
    mutationFn: () => api.post<ChatDetail>('/chat/groups', { name: words, userIds: chosen.map((p) => p.id) }),
    onSuccess: (chat) => {
      qc.setQueryData(chatKeys.one(chat.id), chat);
      refreshGroup(qc);
      toast.success(chosen.length ? t('chat.newGroup.created') : t('chat.newGroup.createdAlone'));
      onBack();
      navigate(`/chat/${chat.id}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const canCreate = words.length > 0 && length <= MAX_GROUP_NAME && !create.isPending;

  if (step === 'name') {
    const names = chosen.map((p) => localized(p)).join(locale === 'ar' ? '، ' : ', ');
    return (
      <div
        className="flex min-h-0 flex-1 flex-col"
        onKeyDown={(e) => {
          if (e.key === 'Escape') setStep('people');
        }}
      >
        <PanelHeader
          title={t('chat.newGroup')}
          start={
            <IconButton label={t('chat.back')} onClick={() => setStep('people')}>
              <ArrowLeft className="h-5 w-5 flip-rtl" aria-hidden />
            </IconButton>
          }
        />
        <form
          className="min-h-0 flex-1 overflow-y-auto px-4 py-6"
          onSubmit={(e) => {
            e.preventDefault();
            if (canCreate) create.mutate();
          }}
        >
          <div className="flex justify-center">
            <GroupAvatar size="xl" />
          </div>
          <label className="mt-6 block">
            <span className="text-body-sm font-medium text-[var(--text-primary)]">{t('chat.newGroup.name')}</span>
            <input
              value={name}
              autoFocus
              dir={name ? 'auto' : undefined}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('chat.newGroup.namePlaceholder')}
              className="mt-1.5 h-10 w-full rounded-md border hairline bg-[var(--surface-sunken)] px-3 text-body text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:border-primary dark:placeholder:text-[var(--text-secondary)]"
            />
          </label>
          <p
            className={clsx('mt-1 text-end text-caption tabular', length > MAX_GROUP_NAME ? 'text-error-fg dark:text-error-dark' : 'text-faint')}
            aria-live="polite"
          >
            {formatNumber(length, locale)}/{formatNumber(MAX_GROUP_NAME, locale)}
          </p>
          <p className="mt-4 rounded-md bg-[var(--surface-sunken)] px-3 py-2.5 text-body-sm text-muted">
            {chosen.length ? <bdi>{t('chat.newGroup.inviting', { names })}</bdi> : t('chat.newGroup.nobody')}
          </p>
          <Button
            type="submit"
            variant="primary"
            className="mt-5 w-full"
            icon={<Check className="h-4 w-4" />}
            loading={create.isPending}
            disabled={!canCreate}
          >
            {t('chat.newGroup.create')}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div
      className="flex min-h-0 flex-1 flex-col"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onBack();
      }}
    >
      <PanelHeader
        title={t('chat.newGroup.people')}
        start={
          <IconButton label={t('chat.back')} onClick={onBack}>
            <ArrowLeft className="h-5 w-5 flip-rtl" aria-hidden />
          </IconButton>
        }
      />
      <div className="shrink-0 space-y-2.5 border-b hairline px-3 py-2.5">
        {chosen.length > 0 && (
          <ul className="flex max-h-24 flex-wrap gap-1.5 overflow-y-auto" aria-label={t('chat.newGroup.chosen', { n: chosen.length })}>
            {chosen.map((person) => (
              <li key={person.id}>
                <button
                  type="button"
                  onClick={() => toggle(person)}
                  aria-label={t('chat.newGroup.untick', { name: localized(person) })}
                  title={t('chat.newGroup.untick', { name: localized(person) })}
                  className="inline-flex max-w-[12rem] items-center gap-1 rounded-pill bg-primary/12 py-0.5 pe-1.5 ps-2.5 text-body-sm font-medium text-primary-600 transition-colors duration-fast hover:bg-primary/20 dark:text-primary-400"
                >
                  <span className="truncate">
                    <bdi>{localized(person)}</bdi>
                  </span>
                  <X className="h-3.5 w-3.5 shrink-0" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        )}
        <SearchBox value={query} onChange={setQuery} placeholder={t('chat.searchPeople')} autoFocus />
        <p className="text-caption text-muted">{t('chat.newGroup.hint')}</p>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto py-1">
        {people.isLoading ? (
          <LoadingBlock label={t('chat.peopleLoading')} />
        ) : people.error ? (
          <ErrorState error={people.error} onRetry={() => void people.refetch()} />
        ) : shown.length === 0 ? (
          <p className="px-6 py-10 text-center text-body-sm text-muted">{t('chat.noPeople')}</p>
        ) : (
          <ul>
            {shown.map((person) => (
              <TickRow
                key={person.id}
                person={person}
                picked={picked.has(person.id)}
                onToggle={() => toggle(person)}
                note={[person.jobTitle, ...person.departments.map((d) => localized(d))].filter(Boolean).join(' · ')}
              />
            ))}
          </ul>
        )}
      </div>
      <div className="flex shrink-0 items-center justify-between gap-3 border-t hairline px-4 py-3">
        <span className="text-body-sm text-muted tabular" aria-live="polite">
          {chosen.length > 0 ? t('chat.newGroup.chosen', { n: formatNumber(chosen.length, locale) }) : ''}
        </span>
        <Button variant="primary" icon={<ArrowRight className="h-4 w-4 flip-rtl" />} onClick={() => setStep('name')}>
          {t('chat.newGroup.next')}
        </Button>
      </div>
    </div>
  );
}

function ChatIntro({ onNewChat }: { onNewChat: () => void }) {
  const { t } = useI18n();
  return (
    <div className="grid flex-1 place-items-center bg-[var(--surface-sunken)] p-8">
      <div className="max-w-sm text-center">
        <span className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-primary/12 text-primary-600 dark:text-primary-400">
          <MessagesSquare className="h-9 w-9" aria-hidden />
        </span>
        <h2 className="mt-5 text-h2 text-[var(--text-primary)]">{t('chat.intro.title')}</h2>
        <p className="mt-2 text-body text-muted">{t('chat.intro.body')}</p>
        <Button className="mt-5" variant="primary" icon={<MessageSquarePlus className="h-4 w-4" />} onClick={onNewChat}>
          {t('chat.new')}
        </Button>
        <p className="mt-6 flex items-center justify-center gap-1.5 text-caption text-faint">
          <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {t('chat.intro.private')}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One chat
// ---------------------------------------------------------------------------

function ChatWindow({ id }: { id: string }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const looking = usePageInView();
  const uploads = useUploads();
  const [viewer, setViewer] = useState<{ pictures: Attachment[]; index: number } | null>(null);
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const [forwarding, setForwarding] = useState<ChatMessage | null>(null);
  // A group's info: beside the chat on a wide window, over it on a narrower one.
  const [info, setInfo] = useState(false);

  const summary = useQuery({
    queryKey: chatKeys.one(id),
    queryFn: () => api.get<ChatDetail>(`/chat/conversations/${id}`),
    initialData: () => qc.getQueryData<ChatList>(chatKeys.list)?.items.find((c) => c.id === id),
    initialDataUpdatedAt: () => qc.getQueryState(chatKeys.list)?.dataUpdatedAt,
    // "online" in the header — and a group's members — kept current.
    refetchInterval: 30_000,
  });
  const thread = useQuery({
    queryKey: chatKeys.thread(id),
    queryFn: () => loadThread(qc, id),
    staleTime: Infinity,
    gcTime: 30 * 60_000,
  });
  const invitations = useInvitations();

  // Looked at again: whatever came while the page was away.
  useEffect(() => {
    if (looking && qc.getQueryData(chatKeys.thread(id))) void catchUp(qc, id);
  }, [looking, id, qc]);

  // Read as it shows: the newest message, while the person looks at the page — as WhatsApp Web reads.
  const newest = thread.data?.items.at(-1)?.seq ?? 0;
  const readUpTo = thread.data?.reads.mine ?? 0;
  useEffect(() => {
    if (looking && newest > readUpTo) void markRead(qc, id, newest).catch(() => undefined);
  }, [looking, newest, readUpTo, id, qc]);

  const failure = summary.error ?? thread.error;
  if (failure) {
    const gone = failure instanceof ApiError && failure.status === 404;
    // A group the caller is invited to: not theirs to read yet — the invitation is, with its answer.
    const invited = gone ? invitations.data?.find((i) => i.group.id === id) : undefined;
    return (
      <>
        <ChatHeader chat={undefined} failed />
        <div className="grid flex-1 place-items-center bg-[var(--surface-sunken)] p-4">
          {invited ? (
            <div className="w-full max-w-sm rounded-lg bg-[var(--surface-card)] py-2 shadow-sm">
              <ul>
                <InvitationRow invitation={invited} />
              </ul>
              <p className="px-4 pb-2 text-caption text-faint">{t('chat.invites.hint')}</p>
            </div>
          ) : gone ? (
            <EmptyState
              title={t('chat.notFound')}
              hint={t('chat.notFoundHint')}
              icon={<MessagesSquare className="h-8 w-8" />}
              action={
                <Link to="/chat" className="text-body-sm font-medium text-primary-600 hover:underline dark:text-primary-400">
                  {t('chat.back')}
                </Link>
              }
            />
          ) : (
            <ErrorState
              error={failure}
              onRetry={() => {
                void summary.refetch();
                void thread.refetch();
              }}
            />
          )}
        </div>
      </>
    );
  }

  const chat = summary.data;
  const group = isGroupChat(chat);
  return (
    <div className="relative flex min-h-0 flex-1">
      <div className="flex min-w-0 flex-1 flex-col">
        <ChatHeader chat={chat} infoOpen={info} onInfo={group ? () => setInfo((v) => !v) : undefined} />
        <DropZone uploads={uploads} className="flex min-h-0 flex-1 flex-col">
          <MessageList
            id={id}
            chat={chat}
            thread={thread.data}
            onOpenPicture={(pictures, index) => setViewer({ pictures, index })}
            onReply={setReplyTo}
            onForward={setForwarding}
          />
          <Composer
            id={id}
            chat={chat}
            ready={!!thread.data}
            uploads={uploads}
            replyTo={replyTo}
            onCancelReply={() => setReplyTo(null)}
          />
        </DropZone>
      </div>
      {group && info && chat && <GroupInfo chat={chat} onClose={() => setInfo(false)} />}
      {viewer && <PictureViewer pictures={viewer.pictures} index={viewer.index} onClose={() => setViewer(null)} />}
      {forwarding && <ForwardDialog message={forwarding} onClose={() => setForwarding(null)} />}
    </div>
  );
}

/** Who is in a group, by name — "You, Mona, Omar" — as WhatsApp Web lists them under its name. */
function memberLine(chat: ChatDetail, t: Translate, localized: Localize, locale: Locale): string {
  if (!chat.members) {
    const n = chat.group?.members ?? 0;
    return n === 1 ? t('chat.group.memberOne') : t('chat.group.membersCount', { n: formatNumber(n, locale) });
  }
  const names = [...chat.members]
    .sort((a, b) => Number(b.me) - Number(a.me) || localized(a).localeCompare(localized(b), locale))
    .map((m) => (m.me ? t('chat.you') : localized(m)));
  return names.join(locale === 'ar' ? '، ' : ', ');
}

function ChatHeader({
  chat,
  infoOpen = false,
  onInfo,
  failed = false,
}: {
  chat: ChatDetail | undefined;
  infoOpen?: boolean;
  /** A group's: opens its info, as a press on WhatsApp's header does. */
  onInfo?: () => void;
  /** Nothing is coming: the chat could not be opened. No spinner, then. */
  failed?: boolean;
}) {
  const { t, locale, localized } = useI18n();
  const group = isGroupChat(chat);
  return (
    <header className="flex h-16 shrink-0 items-center gap-3 border-b hairline bg-[var(--surface-card)] px-2 sm:px-4">
      <Link
        to="/chat"
        aria-label={t('chat.back')}
        title={t('chat.back')}
        className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] md:hidden"
      >
        <ArrowLeft className="h-5 w-5 flip-rtl" aria-hidden />
      </Link>
      {chat && group ? (
        <>
          <button
            type="button"
            onClick={onInfo}
            aria-expanded={infoOpen}
            title={t('chat.group.openInfo')}
            className="flex min-w-0 flex-1 items-center gap-3 rounded-md text-start"
          >
            <GroupAvatar size="md" />
            <span className="min-w-0">
              <span className="block truncate text-title text-[var(--text-primary)]">
                <bdi>{chat.group?.name}</bdi>
              </span>
              <span className="block truncate text-caption text-muted">
                <bdi>{memberLine(chat, t, localized, locale)}</bdi>
              </span>
            </span>
          </button>
          <IconButton label={t('chat.group.info')} pressed={infoOpen} onClick={() => onInfo?.()}>
            <Info className="h-5 w-5" aria-hidden />
          </IconButton>
        </>
      ) : chat?.other ? (
        <>
          <ChatAvatar person={chat.other} size="md" />
          <div className="min-w-0">
            <h2 className="truncate text-title text-[var(--text-primary)]">
              <bdi>{localized(chat.other)}</bdi>
            </h2>
            <p className="truncate text-caption text-muted">
              {!chat.other.isActive ? (
                t('chat.left')
              ) : chat.other.online ? (
                <span className="inline-flex items-center gap-1.5 font-medium text-success-fg dark:text-success">
                  <span className="h-2 w-2 rounded-full bg-status-online" aria-hidden />
                  {t('chat.online')}
                </span>
              ) : (
                (chat.other.jobTitle ?? '')
              )}
            </p>
          </div>
        </>
      ) : (
        !failed && <Spinner />
      )}
    </header>
  );
}

type Row =
  | { kind: 'day'; key: string; label: string }
  | { kind: 'unread'; key: string; count: number }
  | { kind: 'event'; key: string; message: ChatMessage }
  | { kind: 'message'; key: string; message: ChatMessage; first: boolean }
  | { kind: 'outgoing'; key: string; item: Outgoing; first: boolean };

/** Messages a minute or so apart from one person read as one run: no gap, one tail. */
const RUN_MS = 5 * 60_000;

function MessageList({
  id,
  chat,
  thread,
  onOpenPicture,
  onReply,
  onForward,
}: {
  id: string;
  chat: ChatSummary | undefined;
  thread: ThreadState | undefined;
  onOpenPicture: (pictures: Attachment[], index: number) => void;
  onReply: (message: ChatMessage) => void;
  onForward: (message: ChatMessage) => void;
}) {
  const { t, locale, localized } = useI18n();
  const { user } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const box = useRef<HTMLDivElement>(null);
  const [olderLoading, setOlderLoading] = useState(false);
  // Scroll events come faster than renders: the ref keeps a second page from being asked for at once.
  const loadingOlder = useRef(false);
  const [atBottom, setAtBottom] = useState(true);
  const [below, setBelow] = useState(0);
  // The message a quote was clicked for, outlined a moment once it is in view.
  const [flash, setFlash] = useState<string | null>(null);
  const me = user?.id ?? '';
  const group = isGroupChat(chat);
  const otherName = chat?.other ? localized(chat.other) : '';
  /** Who wrote a message, or a quote, or put on a reaction: in a group each by their own name, else the other person. */
  const nameOf = (who: { mine: boolean; sender?: ChatName | null; fullName?: string; fullNameAr?: string | null }) =>
    who.mine
      ? t('chat.you')
      : !group
        ? otherName
        : who.sender
          ? localized(who.sender)
          : who.fullName
            ? localized({ fullName: who.fullName, fullNameAr: who.fullNameAr })
            : t('chat.group.someone');

  /** To the message a reply quotes: in view at once, or after loading older pages until it is. */
  const jumpTo = async (messageId: string, gone: TranslationKey = 'chat.quoteGone') => {
    for (let round = 0; round < 25; round++) {
      const el = box.current?.querySelector<HTMLElement>(`[data-message-id="${messageId}"]`);
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        setFlash(messageId);
        window.setTimeout(() => setFlash((current) => (current === messageId ? null : current)), 1600);
        return;
      }
      if (!qc.getQueryData<ThreadState>(chatKeys.thread(id))?.hasMore) break;
      await loadOlder(qc, id).catch(() => undefined);
      // The older page has to be drawn before it can be looked in.
      await new Promise((resolve) => window.setTimeout(resolve, 80));
    }
    toast.toast(t(gone));
  };

  // Opened from the bell's "mentioned you" (`?m=<message>`): to that message, once the chat is here.
  const [params, setParams] = useSearchParams();
  const focus = params.get('m');
  const loaded = !!thread;
  useEffect(() => {
    if (!focus || !loaded) return;
    void jumpTo(focus, 'chat.mention.gone').finally(() =>
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete('m');
          return next;
        },
        { replace: true },
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, loaded]);

  // The first message not read when the chat opened: the unread line goes above it, and the chat opens there.
  // A group's lines are never unread.
  const firstUnread = useRef<number | null | undefined>(undefined);
  if (firstUnread.current === undefined && thread) {
    const mark = thread.reads.mine;
    firstUnread.current = thread.items.find((m) => !m.mine && !m.event && m.seq > mark)?.seq ?? null;
  }

  const rows = useMemo<Row[]>(() => {
    if (!thread) return [];
    const out: Row[] = [];
    let day = '';
    let previous: { sender: string; at: number } | null = null;
    const unreadCount =
      firstUnread.current != null
        ? thread.items.filter((m) => !m.mine && !m.event && m.seq >= firstUnread.current!).length
        : 0;
    const entries = [
      ...thread.items.map((message) => ({ message, item: null as Outgoing | null, at: new Date(message.createdAt) })),
      ...thread.outbox.map((item) => ({ message: null as ChatMessage | null, item, at: new Date(item.createdAt) })),
    ];
    for (const entry of entries) {
      const key = dayKey(entry.at);
      if (key !== day) {
        out.push({ kind: 'day', key: `day-${key}`, label: dayLabel(entry.at, locale, t) });
        day = key;
        previous = null;
      }
      if (entry.message && entry.message.seq === firstUnread.current) {
        out.push({ kind: 'unread', key: 'unread', count: unreadCount });
        previous = null;
      }
      // A group's own line stands between messages, and the next message starts a run afresh.
      if (entry.message?.event) {
        out.push({ kind: 'event', key: entry.message.id, message: entry.message });
        previous = null;
        continue;
      }
      const sender = entry.message ? entry.message.senderId : me;
      const first = !previous || previous.sender !== sender || entry.at.getTime() - previous.at > RUN_MS;
      out.push(
        entry.message
          ? { kind: 'message', key: entry.message.id, message: entry.message, first }
          : { kind: 'outgoing', key: entry.item!.clientId, item: entry.item!, first },
      );
      previous = { sender, at: entry.at.getTime() };
    }
    return out;
  }, [thread, locale, t, me]);

  // Where the list stands, measured after every change: keeps the reader's place when
  // older messages come in above, and follows new ones when the reader is at the end.
  const snap = useRef({ ready: false, first: 0, count: 0, height: 0, top: 0, atBottom: true });
  useLayoutEffect(() => {
    const el = box.current;
    if (!el || !thread) return;
    const s = snap.current;
    const first = thread.items[0]?.seq ?? 0;
    const count = thread.items.length + thread.outbox.length;
    if (!s.ready) {
      const line = el.querySelector<HTMLElement>('[data-unread-line]');
      el.scrollTop = line ? Math.max(0, line.offsetTop - 72) : el.scrollHeight;
      s.ready = true;
    } else if (first && s.first && first < s.first) {
      el.scrollTop = el.scrollHeight - s.height + s.top;
    } else if (count > s.count) {
      const last = rows.at(-1);
      const mineLast = last?.kind === 'outgoing' || (last?.kind === 'message' && last.message.mine);
      if (s.atBottom || mineLast) el.scrollTop = el.scrollHeight;
      else setBelow((n) => n + (count - s.count));
    }
    s.first = first;
    s.count = count;
    s.height = el.scrollHeight;
    s.top = el.scrollTop;
    s.atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    setAtBottom(s.atBottom);
  }, [rows, thread]);

  const older = () => {
    if (!thread?.hasMore || loadingOlder.current) return;
    loadingOlder.current = true;
    setOlderLoading(true);
    loadOlder(qc, id)
      .catch((e: Error) => toast.error(e.message))
      .finally(() => {
        loadingOlder.current = false;
        setOlderLoading(false);
      });
  };

  // A first page shorter than the window cannot be scrolled up: the next page comes by itself.
  useEffect(() => {
    const el = box.current;
    if (el && thread?.hasMore && !olderLoading && el.scrollHeight <= el.clientHeight + 40) older();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread?.items.length, thread?.hasMore]);

  const onScroll = () => {
    const el = box.current;
    if (!el) return;
    const s = snap.current;
    s.top = el.scrollTop;
    s.height = el.scrollHeight;
    s.atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (s.atBottom !== atBottom) setAtBottom(s.atBottom);
    if (s.atBottom && below) setBelow(0);
    if (el.scrollTop < 240) older();
  };

  const toBottom = () => {
    const el = box.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    setBelow(0);
  };

  const theirs = thread?.reads.theirs ?? 0;
  // Someone no longer an employee: their chat reads, and takes nothing more — reactions neither. A group goes on.
  const canReact = group || chat?.other?.isActive !== false;
  const onReact = (message: ChatMessage, emoji: string | null) =>
    void reactTo(qc, id, message.id, emoji, me).catch((e: Error) => toast.error(e.message));
  // A group's history may begin at the invitation: its very first lines are someone else's to read.
  const fromStart = !group || thread?.items[0]?.event === 'GROUP_CREATED' || rows.length === 0;

  return (
    <div className="relative min-h-0 flex-1">
      <div
        ref={box}
        onScroll={onScroll}
        role="log"
        aria-live="polite"
        aria-label={
          chat
            ? group
              ? t('chat.group.with', { name: chatTitle(chat, localized) })
              : t('chat.with', { name: otherName })
            : t('chat.title')
        }
        className="relative h-full overflow-y-auto bg-[var(--surface-sunken)] px-3 pb-4 pt-2 sm:px-6 lg:px-[6%]"
      >
        {!thread ? (
          <LoadingBlock />
        ) : (
          <>
            {thread.hasMore ? (
              <div className="flex justify-center py-2" aria-hidden={!olderLoading}>
                {olderLoading && (
                  <span className="inline-flex items-center gap-2 rounded-pill bg-[var(--surface-card)] px-3 py-1 text-caption text-muted shadow-sm">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                    {t('chat.loadingOlder')}
                  </span>
                )}
              </div>
            ) : (
              chat && (
                <div className="mx-auto my-3 max-w-md rounded-md bg-[var(--surface-card)] px-4 py-3 text-center shadow-sm">
                  <p className="flex items-center justify-center gap-1.5 text-caption text-muted">
                    <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    <bdi>
                      {group
                        ? fromStart
                          ? t('chat.group.start', { name: chatTitle(chat, localized) })
                          : t('chat.group.startLater', { name: chatTitle(chat, localized) })
                        : t('chat.start', { name: otherName })}
                    </bdi>
                  </p>
                  {rows.every((r) => r.kind !== 'message' && r.kind !== 'outgoing') && (
                    <p className="mt-1 text-caption text-faint">{group ? t('chat.group.startHint') : t('chat.startHint')}</p>
                  )}
                </div>
              )
            )}
            {rows.map((row) => {
              switch (row.kind) {
                case 'day':
                  return (
                    <div key={row.key} className="my-3 flex justify-center">
                      <span className="rounded-md bg-[var(--surface-card)] px-3 py-1 text-caption font-medium text-muted shadow-sm">
                        {row.label}
                      </span>
                    </div>
                  );
                case 'event':
                  return (
                    <div key={row.key} data-message-id={row.message.id} className="my-2 flex justify-center">
                      <span
                        className="max-w-[min(90%,32rem)] rounded-md bg-[var(--surface-card)] px-3 py-1 text-center text-caption text-muted shadow-sm"
                        title={formatDateTime(row.message.createdAt, locale)}
                      >
                        <bdi>{eventText(row.message, me, t, localized)}</bdi>
                      </span>
                    </div>
                  );
                case 'unread':
                  return (
                    <div key={row.key} data-unread-line className="my-3 flex justify-center">
                      <span className="rounded-pill bg-primary/12 px-3 py-1 text-caption font-semibold text-primary-600 dark:text-primary-400">
                        {row.count === 1 ? t('chat.unreadOne') : t('chat.unreadMany', { n: formatNumber(row.count, locale) })}
                      </span>
                    </div>
                  );
                case 'message':
                  return (
                    <Bubble
                      key={row.key}
                      message={row.message}
                      first={row.first}
                      read={row.message.mine && row.message.seq <= theirs}
                      nameOf={nameOf}
                      // In a group, whose it is over the first of a run of someone else's, in their colour.
                      by={group && !row.message.mine && row.first ? nameOf(row.message) : null}
                      flash={flash === row.message.id}
                      canReact={canReact}
                      onReact={onReact}
                      onOpenPicture={onOpenPicture}
                      onReply={onReply}
                      onForward={onForward}
                      onJump={(messageId) => void jumpTo(messageId)}
                    />
                  );
                case 'outgoing':
                  return (
                    <OutgoingBubble
                      key={row.key}
                      id={id}
                      item={row.item}
                      first={row.first}
                      nameOf={nameOf}
                      onOpenPicture={onOpenPicture}
                    />
                  );
              }
            })}
          </>
        )}
      </div>
      {!atBottom && (
        <button
          type="button"
          onClick={toBottom}
          aria-label={t('chat.jumpDown')}
          title={t('chat.jumpDown')}
          className="absolute bottom-4 end-4 grid h-10 w-10 place-items-center rounded-full bg-[var(--surface-card)] text-[var(--text-secondary)] shadow-lg ring-1 ring-black/5 transition-colors duration-fast hover:text-[var(--text-primary)] dark:ring-white/10"
        >
          <ArrowDown className="h-5 w-5" aria-hidden />
          {below > 0 && (
            <span className="absolute -top-1.5 -end-1.5 grid h-5 min-w-5 place-items-center rounded-pill bg-primary px-1 text-[11px] font-bold text-white tabular">
              {below > 99 ? '99+' : below}
            </span>
          )}
        </button>
      )}
    </div>
  );
}

/** A bubble's look: its side's colour, and a sharper corner where a run of messages starts. */
function bubbleClass(mine: boolean, first: boolean) {
  return clsx(
    'relative w-fit min-w-[5.5rem] max-w-[min(85%,36rem)] rounded-lg px-2.5 py-1.5 shadow-sm',
    mine
      ? 'bg-primary-50 text-[var(--text-primary)] ring-1 ring-primary-100 dark:bg-primary-950 dark:ring-primary-900'
      : 'bg-[var(--surface-card)] text-[var(--text-primary)] ring-1 ring-black/5 dark:ring-white/5',
    first && (mine ? 'rounded-se-sm' : 'rounded-ss-sm'),
  );
}

/** "12:40" and, on one's own message, how it has landed: on its way, sent, or read. */
function Meta({
  at,
  state,
  title,
}: {
  at: string;
  state: 'sending' | 'failed' | 'sent' | 'read' | null;
  title?: string;
}) {
  const { t, locale } = useI18n();
  return (
    <span
      className="inline-flex items-center gap-1 whitespace-nowrap text-[11px] leading-4 text-[var(--text-tertiary)] tabular"
      title={title ?? formatDateTime(at, locale)}
    >
      {formatShortTime(at, locale)}
      {state === 'sending' && <Clock className="h-3.5 w-3.5" aria-label={t('chat.sending')} />}
      {state === 'failed' && (
        <CircleAlert className="h-3.5 w-3.5 text-error-fg dark:text-error-dark" aria-label={t('chat.failed')} />
      )}
      {state === 'sent' && <Check className="h-3.5 w-3.5" aria-label={t('chat.sent')} />}
      {state === 'read' && (
        <CheckCheck className="h-3.5 w-3.5 text-info-fg dark:text-info-dark" aria-label={t('chat.read')} />
      )}
    </span>
  );
}

/** Links in a message open in a new tab — http and https only. */
const LINK = /\bhttps?:\/\/[^\s<>"]+[^\s<>".,:;!?'")\]]/gi;

function linkified(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(LINK)) {
    const at = match.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    out.push(
      <a
        key={at}
        href={match[0]}
        target="_blank"
        rel="noopener noreferrer"
        className="break-all text-primary-600 underline underline-offset-2 dark:text-primary-300"
      >
        {match[0]}
      </a>,
    );
    last = at + match[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/**
 * The words, in their own direction, with the time tucked into the last line's
 * end as WhatsApp does: a blank as wide as the time ends the text, and the time
 * sits over it. Its mentions are drawn as links (MentionLink).
 */
function MessageText({
  text,
  meta,
  mine,
  mentions,
}: {
  text: string;
  meta: ReactNode;
  mine: boolean;
  mentions?: ChatMention[];
}) {
  const named = (mentions?.length ?? 0) > 0;
  const big = !named && onlyEmoji(text);
  return (
    <div className="relative" dir="auto">
      <p
        className={clsx(
          'whitespace-pre-wrap [overflow-wrap:anywhere]',
          big ? 'py-0.5 text-[2.5rem] leading-tight' : 'text-body text-[var(--text-primary)]',
        )}
      >
        {named
          ? splitMentions(text, mentions).map((piece, i) =>
              piece.mention ? (
                <MentionLink key={i} mention={piece.mention} text={piece.text} />
              ) : (
                <Fragment key={i}>{linkified(piece.text)}</Fragment>
              ),
            )
          : linkified(text)}
        <span className={clsx('inline-block', mine ? 'w-[4.75rem]' : 'w-12')} aria-hidden />
      </p>
      <span className="absolute -bottom-0.5 end-0">{meta}</span>
    </div>
  );
}

/**
 * A mention in a message (operator, 2026-10-22: "use @ for mention person and #
 * to mention task or ticket"), drawn in the words as it was written. Someone:
 * pressed, opens the reader's chat with them; the reader's own name is marked,
 * as WhatsApp marks it. A task or a ticket: opens its page — one the reader may
 * not open, or one deleted, stays words, with a word on why.
 */
function MentionLink({ mention, text }: { mention: ChatMention; text: string }) {
  const { t, locale, localized } = useI18n();
  const open = useOpenChat();
  if (mention.type === 'USER') {
    const name = mention.fullName ? localized({ fullName: mention.fullName, fullNameAr: mention.fullNameAr }) : text.slice(1);
    const look = clsx(
      'rounded-sm font-semibold',
      mention.me
        ? 'bg-primary/15 px-0.5 text-primary-700 dark:bg-primary/25 dark:text-primary-200'
        : 'text-primary-600 dark:text-primary-300',
    );
    if (mention.me || mention.isActive === false) {
      return (
        <span className={look} title={mention.me ? t('chat.mention.you') : t('chat.mention.left', { name })}>
          <bdi>{text}</bdi>
        </span>
      );
    }
    return (
      <button
        type="button"
        onClick={() => open.mutate(mention.id)}
        disabled={open.isPending}
        title={t('chat.mention.message', { name })}
        className={clsx(look, 'hover:underline disabled:cursor-wait')}
      >
        <bdi>{text}</bdi>
      </button>
    );
  }
  if (!mention.canOpen) {
    return (
      <span
        className="font-medium text-[var(--text-secondary)] underline decoration-dotted underline-offset-2"
        title={mention.type === 'TASK' ? t('chat.mention.cannotOpenTask') : t('chat.mention.cannotOpenTicket')}
      >
        <bdi>{text}</bdi>
      </span>
    );
  }
  const status = mention.status ? enumLabel(mention.status, locale) : '';
  return (
    <Link
      to={referencePath(mention)}
      title={
        mention.type === 'TICKET'
          ? t('chat.mention.openTicket', { number: mention.number ?? '', title: mention.title ?? '', status })
          : t('chat.mention.openTask', { title: mention.title ?? '', status })
      }
      className="font-semibold text-primary-600 hover:underline dark:text-primary-300"
    >
      <bdi>{text}</bdi>
    </Link>
  );
}

/** A message's pictures: one large, or up to four in a square, the rest counted on the last. */
function PictureGrid({ pictures, onOpen }: { pictures: Attachment[]; onOpen: (index: number) => void }) {
  const { t } = useI18n();
  if (pictures.length === 1) {
    const p = pictures[0];
    return (
      <button
        type="button"
        onClick={() => onOpen(0)}
        className="mb-1 block overflow-hidden rounded-md"
        aria-label={t('chat.viewer.open', { name: p.file.originalName })}
      >
        <img src={p.file.url} alt={p.file.originalName} loading="lazy" className="block h-60 w-72 max-w-full object-cover" />
      </button>
    );
  }
  const shown = pictures.slice(0, 4);
  const more = pictures.length - shown.length;
  return (
    <div className="mb-1 grid w-72 max-w-full grid-cols-2 gap-1">
      {shown.map((p, i) => (
        <button
          key={p.id}
          type="button"
          onClick={() => onOpen(i)}
          className="relative block aspect-square overflow-hidden rounded-md"
          aria-label={t('chat.viewer.open', { name: p.file.originalName })}
        >
          <img src={p.file.url} alt={p.file.originalName} loading="lazy" className="block h-full w-full object-cover" />
          {i === shown.length - 1 && more > 0 && (
            <span className="absolute inset-0 grid place-items-center bg-black/55 text-h3 font-semibold text-white" dir="ltr">
              +{more}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

function BubbleFiles({ attachments, onOpenPicture }: { attachments: Attachment[]; onOpenPicture: (pictures: Attachment[], index: number) => void }) {
  const pictures = attachments.filter((a) => isPicture(a.file.mimeType));
  const rest = attachments.filter((a) => !isPicture(a.file.mimeType));
  return (
    <>
      {pictures.length > 0 && <PictureGrid pictures={pictures} onOpen={(index) => onOpenPicture(pictures, index)} />}
      {rest.length > 0 && <AttachmentList attachments={rest} className="mb-1 w-72 max-w-full" />}
    </>
  );
}

/**
 * The message a reply answers, above it — and above the message box while the
 * reply is written. One said in a group before the reader was invited shows who
 * wrote it and nothing more, and leads nowhere: it is not theirs to read.
 */
function QuoteCard({ quote, name, onClick }: { quote: ChatQuote; name: string; onClick?: () => void }) {
  const { t } = useI18n();
  const { icon, label } = carriedLabel(quote, t);
  if (quote.hidden) onClick = undefined;
  const inside = (
    <>
      <span className="min-w-0 flex-1 px-2 py-1">
        <span className="block truncate text-caption font-semibold text-primary-600 dark:text-primary-300">
          <bdi>{name}</bdi>
        </span>
        <span className={clsx('flex min-w-0 items-center gap-1 text-caption text-muted', quote.hidden && 'italic')}>
          {!quote.hidden && icon}
          <span className="truncate" dir="auto">
            {quote.hidden ? t('chat.group.earlier') : (quote.body ?? label)}
          </span>
        </span>
      </span>
      {quote.picture && (
        <img
          src={quote.picture.url}
          alt=""
          className={clsx('h-12 w-12 shrink-0', quote.sticker ? 'object-contain p-0.5' : 'object-cover')}
        />
      )}
    </>
  );
  const look =
    'flex w-full min-w-0 items-stretch overflow-hidden rounded-md border-s-4 border-primary bg-black/[0.04] text-start dark:bg-white/[0.06]';
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      title={t('chat.quoteJump')}
      className={clsx(look, 'mb-1 transition-colors duration-fast hover:bg-black/[0.07] dark:hover:bg-white/[0.1]')}
    >
      {inside}
    </button>
  ) : (
    <div className={look}>{inside}</div>
  );
}

/**
 * A panel by the control that opened it — on the side it prefers when there is
 * room, else the other, always inside the window. It is drawn over the page, so
 * the chat's own scrolling never cuts it off. A press anywhere else closes it,
 * and so does Escape, which hands the focus back to its control; so does the
 * chat scrolling or the window changing size, which would leave it adrift.
 */
function Floating({
  anchor,
  onClose,
  label,
  prefer = 'above',
  align = 'center',
  children,
}: {
  anchor: HTMLElement;
  onClose: () => void;
  /** Named as a dialog — left out when what it holds is one already. */
  label?: string;
  prefer?: 'above' | 'below';
  /** Lined up with the control's middle, or with its start or end edge in the reading direction. */
  align?: 'center' | 'start' | 'end';
  children: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{ top: number; left: number } | null>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useLayoutEffect(() => {
    const el = panel.current;
    if (!el) return;
    const at = anchor.getBoundingClientRect();
    const { width, height } = el.getBoundingClientRect();
    const gap = 6;
    const edge = 8;
    const above = at.top - gap - height;
    const below = at.bottom + gap;
    const fitsAbove = above >= edge;
    const fitsBelow = below + height <= window.innerHeight - edge;
    const top = prefer === 'above' ? (fitsAbove || !fitsBelow ? above : below) : fitsBelow || !fitsAbove ? below : above;
    const rtl = getComputedStyle(anchor).direction === 'rtl';
    const fromStart = rtl ? at.right - width : at.left;
    const fromEnd = rtl ? at.left : at.right - width;
    const wanted = align === 'start' ? fromStart : align === 'end' ? fromEnd : at.left + at.width / 2 - width / 2;
    const left = Math.min(wanted, window.innerWidth - width - edge);
    setPlace({ top: Math.max(edge, Math.min(top, window.innerHeight - height - edge)), left: Math.max(edge, left) });
  }, [anchor, prefer, align]);

  useEffect(() => {
    const away = (e: Event) => {
      // The emoji list scrolls inside the panel: that is not the chat moving.
      if (e.type === 'scroll' && panel.current?.contains(e.target as Node)) return;
      close.current();
    };
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!panel.current?.contains(target) && !anchor.contains(target)) close.current();
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      close.current();
      anchor.focus();
    };
    window.addEventListener('resize', away);
    document.addEventListener('scroll', away, true);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', away);
      document.removeEventListener('scroll', away, true);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchor]);

  // Once it is where it goes — hidden while it was measured, it could take no focus — what asks for it gets it.
  const placed = place !== null;
  useEffect(() => {
    if (placed) panel.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus({ preventScroll: true });
  }, [placed]);

  return createPortal(
    <div
      ref={panel}
      role={label ? 'dialog' : undefined}
      aria-label={label}
      className="fixed z-dropdown"
      style={place ?? { top: 0, left: 0, visibility: 'hidden' }}
    >
      {children}
    </div>,
    document.body,
  );
}

/**
 * The reactions offered at once — WhatsApp's six — and the rest of the emoji a
 * press further. The one already given is marked, and pressing it again takes
 * it off; one picked from the rest shows beside the six.
 */
function ReactionTray({
  current,
  onPick,
  onMore,
  itemRole,
  autoFocus = false,
  className,
}: {
  current: string | null;
  onPick: (emoji: string | null) => void;
  onMore: () => void;
  /** Inside the message's menu, each is one of its items. */
  itemRole?: 'menuitem';
  autoFocus?: boolean;
  className?: string;
}) {
  const { t } = useI18n();
  const shown: string[] = [...QUICK_REACTIONS];
  if (current && !shown.includes(current)) shown.push(current);
  return (
    <div className={clsx('flex items-center gap-0.5', className)}>
      {shown.map((emoji, i) => {
        const given = emoji === current;
        const label = given ? t('chat.reactionTakeOff', { emoji }) : t('chat.reactWith', { emoji });
        return (
          <button
            key={emoji}
            type="button"
            role={itemRole}
            aria-pressed={itemRole ? undefined : given}
            aria-label={label}
            title={label}
            data-autofocus={autoFocus && i === 0 ? '' : undefined}
            onClick={() => onPick(given ? null : emoji)}
            className={clsx(
              'grid h-8 w-8 shrink-0 place-items-center rounded-full text-[20px] leading-none transition-transform duration-fast hover:scale-110 focus-visible:scale-110',
              given ? 'bg-primary/15 ring-1 ring-primary/40' : 'hover:bg-[var(--surface-sunken)]',
            )}
          >
            {emoji}
          </button>
        );
      })}
      <button
        type="button"
        role={itemRole}
        aria-label={t('chat.reactMore')}
        title={t('chat.reactMore')}
        onClick={onMore}
        className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-[var(--text-secondary)] transition-colors duration-fast hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
      >
        <Plus className="h-5 w-5" aria-hidden />
      </button>
    </div>
  );
}

/** Every emoji, to react with — picking the one already given takes it off. */
function ReactionPicker({ current, onReact }: { current: string | null; onReact: (emoji: string | null) => void }) {
  return (
    <EmojiPicker
      className="h-80 w-[min(20rem,calc(100vw-1rem))]"
      onPick={(emoji) => onReact(emoji === current ? null : emoji)}
    />
  );
}

/**
 * Beside a message, on hover, as on WhatsApp Web: a smiley that offers the
 * reactions. A touch screen has no hover — its reactions are in the menu.
 */
function ReactControl({ current, onReact }: { current: string | null; onReact: (emoji: string | null) => void }) {
  const { t } = useI18n();
  const [open, setOpen] = useState<'quick' | 'all' | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const react = (emoji: string | null) => {
    setOpen(null);
    onReact(emoji);
  };
  return (
    <>
      <button
        ref={button}
        type="button"
        onClick={() => setOpen((v) => (v ? null : 'quick'))}
        aria-haspopup="dialog"
        aria-expanded={!!open}
        aria-label={t('chat.react')}
        title={t('chat.react')}
        className={clsx(
          'grid h-7 w-7 shrink-0 place-items-center rounded-full text-[var(--text-tertiary)] transition-opacity duration-fast hover:bg-[var(--surface-card)] hover:text-[var(--text-secondary)] [@media(hover:none)]:hidden',
          open ? 'opacity-100' : 'opacity-0 focus-visible:opacity-100 group-hover/row:opacity-100',
        )}
      >
        <SmilePlus className="h-[18px] w-[18px]" aria-hidden />
      </button>
      {open && button.current && (
        <Floating
          key={open}
          anchor={button.current}
          onClose={() => setOpen(null)}
          label={open === 'quick' ? t('chat.react') : undefined}
        >
          {open === 'quick' ? (
            <ReactionTray
              current={current}
              autoFocus
              onPick={react}
              onMore={() => setOpen('all')}
              className="rounded-pill surface p-1 shadow-xl"
            />
          ) : (
            <ReactionPicker current={current} onReact={react} />
          )}
        </Floating>
      )}
    </>
  );
}

/**
 * A message's reactions, hanging from its foot: each emoji once, and how many
 * there are when more than one reacted. Pressed, it says who gave which — and
 * one's own comes off from there.
 */
function Reactions({
  reactions,
  nameOf,
  onRemove,
}: {
  reactions: ChatReaction[];
  /** Whose a reaction is: "You", the other person, or — in a group — each by name. */
  nameOf: (reaction: ChatReaction) => string;
  /** Takes one's own off — none in a chat that takes nothing more. */
  onRemove: (() => void) | null;
}) {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const emoji = [...new Set(reactions.map((r) => r.emoji))];
  const who = nameOf;
  const summary = reactions.map((r) => `${who(r)} ${r.emoji}`).join(locale === 'ar' ? '، ' : ', ');
  return (
    <>
      <button
        ref={button}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={t('chat.reactionsLabel', { list: summary })}
        title={summary}
        className="absolute -bottom-4 start-2 z-[1] inline-flex h-6 items-center gap-0.5 rounded-pill bg-[var(--surface-card)] px-1.5 text-[15px] leading-none shadow-sm ring-1 ring-black/10 transition-transform duration-fast hover:scale-105 dark:ring-white/10"
      >
        {emoji.map((e) => (
          <span key={e}>{e}</span>
        ))}
        {reactions.length > 1 && (
          <span className="ms-0.5 text-[11px] font-semibold text-[var(--text-secondary)] tabular">
            {formatNumber(reactions.length, locale)}
          </span>
        )}
      </button>
      {open && button.current && (
        <Floating anchor={button.current} onClose={() => setOpen(false)} label={t('chat.reactions')}>
          <div className="w-60 overflow-hidden rounded-lg surface py-1 shadow-xl">
            <p className="px-3 pb-1 pt-1.5 text-overline uppercase text-[var(--text-tertiary)]">{t('chat.reactions')}</p>
            <ul>
              {reactions.map((r) => {
                const removable = r.mine && !!onRemove;
                const inside = (
                  <>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-body-sm font-medium text-[var(--text-primary)]">
                        <bdi>{who(r)}</bdi>
                      </span>
                      {removable && <span className="block text-caption text-muted">{t('chat.reactionRemove')}</span>}
                    </span>
                    <span className="text-[22px] leading-none">{r.emoji}</span>
                  </>
                );
                return (
                  <li key={r.userId}>
                    {removable ? (
                      <button
                        type="button"
                        data-autofocus=""
                        onClick={() => {
                          setOpen(false);
                          onRemove!();
                        }}
                        className="flex w-full items-center gap-3 px-3 py-2 text-start transition-colors duration-fast hover:bg-[var(--surface-sunken)]"
                      >
                        {inside}
                      </button>
                    ) : (
                      <div className="flex items-center gap-3 px-3 py-2">{inside}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </Floating>
      )}
    </>
  );
}

/**
 * A message's menu, as WhatsApp's: a chevron at its corner — shown on hover and
 * on focus, and always on a touch screen — with the reactions on top, then
 * Reply and Forward. It opens below the chevron, or above it when the window
 * has no room below, and is drawn over the page so the chat never cuts it off.
 */
function MessageMenu({
  mine,
  reaction,
  onReply,
  onForward,
}: {
  mine: boolean;
  /** The reaction already given, and how to change it — null in a chat that takes nothing more. */
  reaction: { current: string | null; onReact: (emoji: string | null) => void } | null;
  onReply: () => void;
  onForward: () => void;
}) {
  const { t } = useI18n();
  // The menu, or every emoji to react with — the menu's "+" opens those in its place.
  const [open, setOpen] = useState<'menu' | 'emoji' | null>(null);
  const button = useRef<HTMLButtonElement>(null);

  const pick = (action: () => void) => {
    setOpen(null);
    action();
  };
  const item =
    'flex w-full items-center gap-2.5 px-3 py-2 text-start text-body-sm text-[var(--text-primary)] transition-colors duration-fast hover:bg-[var(--surface-sunken)]';

  return (
    <div className="absolute end-1 top-1 z-[2]">
      <button
        ref={button}
        type="button"
        onClick={() => setOpen((v) => (v ? null : 'menu'))}
        aria-haspopup="menu"
        aria-expanded={open === 'menu'}
        aria-label={t('chat.menu')}
        title={t('chat.menu')}
        className={clsx(
          'grid h-6 w-6 place-items-center rounded-full text-[var(--text-secondary)] shadow-sm transition-opacity duration-fast',
          mine ? 'bg-primary-50 dark:bg-primary-950' : 'bg-[var(--surface-card)]',
          open ? 'opacity-100' : 'opacity-0 focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100',
        )}
      >
        <ChevronDown className="h-4 w-4" aria-hidden />
      </button>
      {open && button.current && (
        <Floating
          key={open}
          anchor={button.current}
          onClose={() => setOpen(null)}
          prefer={open === 'menu' ? 'below' : 'above'}
          align={mine ? 'end' : 'start'}
        >
          {open === 'menu' ? (
            <div
              role="menu"
              aria-label={t('chat.menu')}
              className={clsx('overflow-hidden rounded-md surface py-1 shadow-lg', reaction ? 'w-72' : 'w-44')}
            >
              {reaction && (
                <ReactionTray
                  current={reaction.current}
                  itemRole="menuitem"
                  onPick={(emoji) => pick(() => reaction.onReact(emoji))}
                  onMore={() => setOpen('emoji')}
                  className="mb-1 border-b hairline px-1.5 pb-1.5 pt-0.5"
                />
              )}
              <button type="button" role="menuitem" onClick={() => pick(onReply)} className={item}>
                <Reply className="h-4 w-4 shrink-0 flip-rtl" aria-hidden />
                {t('chat.reply')}
              </button>
              <button type="button" role="menuitem" onClick={() => pick(onForward)} className={item}>
                <Forward className="h-4 w-4 shrink-0 flip-rtl" aria-hidden />
                {t('chat.forward')}
              </button>
            </div>
          ) : (
            reaction && <ReactionPicker current={reaction.current} onReact={(emoji) => pick(() => reaction.onReact(emoji))} />
          )}
        </Floating>
      )}
    </div>
  );
}

function Bubble({
  message,
  first,
  read,
  nameOf,
  by,
  flash,
  canReact,
  onReact,
  onOpenPicture,
  onReply,
  onForward,
  onJump,
}: {
  message: ChatMessage;
  first: boolean;
  read: boolean;
  /** Whose a quote or a reaction is: "You", the other person, or — in a group — each by name. */
  nameOf: (who: { mine: boolean; sender?: ChatName | null; fullName?: string; fullNameAr?: string | null }) => string;
  /** In a group: the writer's name over the first of their run. */
  by: string | null;
  /** Just jumped to from a quote: outlined a moment. */
  flash: boolean;
  /** The chat takes reactions: a group, or two people's whose other is still an employee. */
  canReact: boolean;
  onReact: (message: ChatMessage, emoji: string | null) => void;
  onOpenPicture: (pictures: Attachment[], index: number) => void;
  onReply: (message: ChatMessage) => void;
  onForward: (message: ChatMessage) => void;
  onJump: (messageId: string) => void;
}) {
  const { t } = useI18n();
  const meta = <Meta at={message.createdAt} state={message.mine ? (read ? 'read' : 'sent') : null} />;
  const reactions = message.reactions ?? [];
  const reaction = canReact
    ? { current: reactions.find((r) => r.mine)?.emoji ?? null, onReact: (emoji: string | null) => onReact(message, emoji) }
    : null;
  const menu = (
    <MessageMenu
      mine={message.mine}
      reaction={reaction}
      onReply={() => onReply(message)}
      onForward={() => onForward(message)}
    />
  );
  const name = by && (
    <p className={clsx('mb-0.5 truncate pe-7 text-caption font-semibold', senderTone(message.senderId))}>
      <bdi>{by}</bdi>
    </p>
  );
  const above = (
    <>
      {message.forwarded && (
        <p className="mb-0.5 flex items-center gap-1 pe-7 text-caption italic text-muted">
          <Forward className="h-3.5 w-3.5 shrink-0 flip-rtl" aria-hidden />
          {t('chat.forwarded')}
        </p>
      )}
      {message.replyTo && (
        <QuoteCard quote={message.replyTo} name={nameOf(message.replyTo)} onClick={() => onJump(message.replyTo!.id)} />
      )}
    </>
  );
  const pill = reactions.length > 0 && (
    <Reactions reactions={reactions} nameOf={nameOf} onRemove={reaction ? () => reaction.onReact(null) : null} />
  );
  const row = clsx(
    // One's own on the far side; the smiley that reacts sits beside the bubble, towards the middle.
    'group/row flex items-center gap-1',
    message.mine && 'flex-row-reverse',
    first ? 'mt-2' : 'mt-0.5',
    reactions.length > 0 && 'mb-4',
  );
  const flashed = flash ? 'outline outline-2 outline-offset-2 outline-primary/60' : 'outline-transparent';

  // A sticker stands on its own, without a bubble; what it forwards or answers keeps one above it.
  if (message.sticker) {
    return (
      <div data-message-id={message.id} className={row}>
        <div
          className={clsx(
            'group relative flex min-w-0 max-w-[min(85%,36rem)] flex-col gap-1 rounded-lg transition-[outline-color] duration-base',
            message.mine ? 'items-end' : 'items-start',
            flashed,
          )}
        >
          {menu}
          {(name || message.forwarded || message.replyTo) && (
            <div className={clsx(bubbleClass(message.mine, first), 'max-w-full')}>
              {name}
              {above}
            </div>
          )}
          <StickerImage sticker={message.sticker} meta={meta} />
          {pill}
        </div>
        {reaction && <ReactControl current={reaction.current} onReact={reaction.onReact} />}
      </div>
    );
  }

  return (
    <div
      data-message-id={message.id}
      className={row}
    >
      <div className={clsx(bubbleClass(message.mine, first), 'group transition-[outline-color] duration-base', flashed)}>
        {menu}
        {name}
        {above}
        <BubbleFiles attachments={message.attachments} onOpenPicture={onOpenPicture} />
        {message.body ? (
          <MessageText text={message.body} meta={meta} mine={message.mine} mentions={message.mentions} />
        ) : (
          <div className="flex justify-end">{meta}</div>
        )}
        {pill}
      </div>
      {reaction && <ReactControl current={reaction.current} onReact={reaction.onReact} />}
    </div>
  );
}

function OutgoingBubble({
  id,
  item,
  first,
  nameOf,
  onOpenPicture,
}: {
  id: string;
  item: Outgoing;
  first: boolean;
  /** Whose the quoted message is: "You", the other person, or — in a group — its writer. */
  nameOf: (who: { mine: boolean; sender?: ChatName | null }) => string;
  onOpenPicture: (pictures: Attachment[], index: number) => void;
}) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const failed = item.status === 'failed';
  const meta = <Meta at={item.createdAt} state={item.status} title={failed ? item.error : undefined} />;
  const quote = item.quote && <QuoteCard quote={item.quote} name={nameOf(item.quote)} />;
  const failure = failed && (
    <div className="mt-1.5 flex flex-wrap items-center justify-end gap-x-3 gap-y-1 text-caption">
      <span className="min-w-0 text-error-fg dark:text-error-dark" title={item.error}>
        {item.error ? `${t('chat.failed')} — ${item.error}` : t('chat.failed')}
      </span>
      <button
        type="button"
        onClick={() => void deliver(qc, id, item)}
        className="font-semibold text-primary-600 hover:underline dark:text-primary-300"
      >
        {t('chat.retry')}
      </button>
      <button type="button" onClick={() => discard(qc, id, item.clientId)} className="text-muted hover:underline">
        {t('chat.discard')}
      </button>
    </div>
  );

  if (item.sticker) {
    return (
      <div className={clsx('flex justify-end', first ? 'mt-2' : 'mt-0.5')}>
        <div className="flex min-w-0 max-w-[min(85%,36rem)] flex-col items-end gap-1">
          {quote && <div className={clsx(bubbleClass(true, first), 'max-w-full')}>{quote}</div>}
          <StickerImage sticker={item.sticker} meta={meta} />
          {failure}
        </div>
      </div>
    );
  }

  return (
    <div className={clsx('flex justify-end', first ? 'mt-2' : 'mt-0.5')}>
      <div className={clsx(bubbleClass(true, first), failed && 'ring-error/50 dark:ring-error/50')}>
        {quote}
        {item.attachments && <BubbleFiles attachments={item.attachments} onOpenPicture={onOpenPicture} />}
        {item.voice && (
          <VoiceNotePlayer src={item.voice.url} durationMs={item.voice.durationMs} className="mb-1 w-64 max-w-full" />
        )}
        {item.body ? (
          <MessageText text={item.body} meta={meta} mine mentions={item.mentions} />
        ) : (
          <div className="flex justify-end">{meta}</div>
        )}
        {failure}
      </div>
    </div>
  );
}

/**
 * A sticker in the chat: its picture alone — no bubble — at 160 px, with the
 * time on a small pill at its foot's end, as WhatsApp shows one: just under the
 * picture, so it covers none of a sticker's words.
 */
function StickerImage({ sticker, meta }: { sticker: ChatSticker; meta: ReactNode }) {
  const { t } = useI18n();
  return (
    <div className="flex w-40 flex-col items-end">
      <img
        src={sticker.url}
        alt={t('chat.stickerAlt', { label: sticker.label })}
        title={sticker.label}
        loading="lazy"
        draggable={false}
        className="block h-40 w-40 select-none object-contain"
      />
      <span className="-mt-1 rounded-pill bg-[var(--surface-card)] px-1.5 py-px shadow-sm ring-1 ring-black/5 dark:ring-white/10">
        {meta}
      </span>
    </div>
  );
}

/** A message's pictures, one at a time over the whole window; arrows (or the keys) go through them. */
function PictureViewer({ pictures, index, onClose }: { pictures: Attachment[]; index: number; onClose: () => void }) {
  const { t, dir } = useI18n();
  const [at, setAt] = useState(index);
  const closeButton = useRef<HTMLButtonElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const count = pictures.length;

  useEffect(() => {
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeButton.current?.focus();
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
      // The way the pictures read, as the page reads: right to left in Arabic.
      const next = dir === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
      const previous = dir === 'rtl' ? 'ArrowRight' : 'ArrowLeft';
      if (e.key === next) setAt((n) => Math.min(count - 1, n + 1));
      if (e.key === previous) setAt((n) => Math.max(0, n - 1));
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      before?.focus();
    };
  }, [count, dir]);

  const picture = pictures[at];
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={picture.file.originalName} className="fixed inset-0 z-modal flex flex-col bg-black/90">
      <div className="flex shrink-0 items-center gap-2 px-3 py-2.5 text-white sm:px-5">
        <p className="min-w-0 flex-1 truncate text-body-sm">
          <bdi>{picture.file.originalName}</bdi>
          {count > 1 && <span className="text-white/70"> · {t('chat.viewer.position', { i: at + 1, n: count })}</span>}
        </p>
        <a
          href={picture.file.downloadUrl}
          aria-label={t('attachments.download')}
          title={t('attachments.download')}
          className="grid h-10 w-10 place-items-center rounded-full hover:bg-white/10"
        >
          <Download className="h-5 w-5" aria-hidden />
        </a>
        <button
          ref={closeButton}
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
          title={t('common.close')}
          className="grid h-10 w-10 place-items-center rounded-full hover:bg-white/10"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>
      </div>
      <div
        className="relative flex min-h-0 flex-1 items-center justify-center px-4 pb-6 sm:px-16"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <img src={picture.file.url} alt={picture.file.originalName} className="max-h-full max-w-full rounded-sm object-contain" />
        {count > 1 && (
          <>
            <button
              type="button"
              onClick={() => setAt((n) => Math.max(0, n - 1))}
              disabled={at === 0}
              aria-label={t('chat.viewer.previous')}
              className="absolute start-2 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 disabled:opacity-30"
            >
              <ChevronLeft className="h-6 w-6 flip-rtl" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => setAt((n) => Math.min(count - 1, n + 1))}
              disabled={at === count - 1}
              aria-label={t('chat.viewer.next')}
              className="absolute end-2 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 disabled:opacity-30"
            >
              <ChevronRight className="h-6 w-6 flip-rtl" aria-hidden />
            </button>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Writing
// ---------------------------------------------------------------------------

/**
 * The message box, as WhatsApp's: emoji, files and pictures (picked, dropped on
 * the chat or pasted), and the microphone while nothing is typed — the send
 * arrow once something is. Enter sends and Shift+Enter starts a new line — or,
 * by the person's choice, Enter starts a new line and Ctrl+Enter sends. A reply
 * shows what it answers above the box until it is sent or let go.
 */
function Composer({
  id,
  chat,
  ready,
  uploads,
  replyTo,
  onCancelReply,
}: {
  id: string;
  chat: ChatDetail | undefined;
  ready: boolean;
  uploads: Uploads;
  replyTo: ChatMessage | null;
  onCancelReply: () => void;
}) {
  const { t, localized, locale } = useI18n();
  const { user, can } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  // Whose draft it is as well as where: someone else signing in on this tab never finds it.
  const draftKey = `${user?.id ?? ''}:${id}`;
  const [text, setText] = useState(() => drafts.get(draftKey) ?? '');
  // The people, tasks and tickets the words mention — where each name is in them.
  const [mentions, setMentions] = useState<ChatMention[]>(() => draftMentions.get(draftKey) ?? []);
  // Where the caret is, nothing selected: the "@" or "#" being typed is looked for before it.
  const [caret, setCaret] = useState<number | null>(null);
  const [focused, setFocused] = useState(false);
  // The "@" or "#" whose list Escape closed: it stays closed for that one.
  const [dismissed, setDismissed] = useState<number | null>(null);
  const [active, setActive] = useState(0);
  const [recording, setRecording] = useState(false);
  // The emoji, or the stickers — one panel at a time, over the box.
  const [panel, setPanel] = useState<'emoji' | 'stickers' | null>(null);
  const tray = useStickerTray();
  const packs = tray.data?.packs ?? [];
  const managesStickers = can(P.CHAT_STICKERS_MANAGE);
  // Offered once there is a sticker to send — and to whoever puts the packs up, who finds the way to them there.
  const offersStickers = packs.length > 0 || managesStickers;
  const area = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const pickerBox = useRef<HTMLDivElement>(null);
  const listId = useId();
  const [enterSends] = useEnterSends();

  // Reply chosen: the box takes the focus, for the answer.
  useEffect(() => {
    if (replyTo) area.current?.focus();
  }, [replyTo]);

  /** What a message sent now answers, if anything — then the reply is done. */
  const answering = () => {
    if (!replyTo) return {};
    onCancelReply();
    return { replyToId: replyTo.id, quote: quoteOf(replyTo) };
  };

  const uploaded = uploads.items.filter((i) => i.status === 'done' && i.file);
  const words = text.trim();
  const canSend = ready && !uploads.busy && (words.length > 0 || uploaded.length > 0);
  const nothing = words.length === 0 && uploads.items.length === 0;

  const update = (value: string, named: ChatMention[] = []) => {
    setText(value);
    setMentions(named);
    if (value) drafts.set(draftKey, value);
    else drafts.delete(draftKey);
    if (value && named.length) draftMentions.set(draftKey, named);
    else draftMentions.delete(draftKey);
  };

  // ---- Mentions (operator, 2026-10-22: "use @ for mention person and # to mention task or ticket") ----
  const typed = focused && caret !== null ? activeTrigger(text, caret, mentions) : null;
  const trigger = typed && typed.start !== dismissed ? typed : null;
  const typing = typed !== null;
  useEffect(() => {
    if (!typing) setDismissed(null);
  }, [typing]);

  // "@": in a group, its members alone (operator, 2026-10-22: "in group chat when mention employee must one of
  // group employees"); in two people's chat, the other person first, then everyone else.
  const group = isGroupChat(chat);
  const people = useQuery({
    queryKey: chatKeys.people,
    queryFn: () => api.get<ChatPersonOption[]>('/chat/people'),
    staleTime: 30_000,
    enabled: trigger?.sign === '@' && !group,
  });
  const matches = useMatcher(trigger?.sign === '@' ? trigger.query : '');
  // "#": asked of the API, a moment after the typing stops.
  const [refQuery, setRefQuery] = useState('');
  useEffect(() => {
    if (trigger?.sign !== '#') return;
    const timer = window.setTimeout(() => setRefQuery(trigger.query), 150);
    return () => window.clearTimeout(timer);
  }, [trigger?.sign, trigger?.query]);
  const references = useReferences(refQuery, trigger?.sign === '#');

  const options = useMemo<MentionOption[]>(() => {
    if (!trigger) return [];
    if (trigger.sign === '#') {
      return (references.data?.items ?? []).map((r) => ({
        key: `${r.type}:${r.id}`,
        label: r.label,
        reference: r,
        mention: { type: r.type, id: r.id, canOpen: true, title: r.title, status: r.status, number: r.number, leader: r.leader },
      }));
    }
    const byName = (a: { fullName: string; fullNameAr?: string | null }, b: typeof a) =>
      localized(a).localeCompare(localized(b), locale);
    const own = group
      ? (chat?.members ?? []).filter((m) => !m.me && m.isActive).sort(byName)
      : chat?.other?.isActive
        ? [chat.other]
        : [];
    const ownIds = new Set(own.map((p) => p.id));
    const rest = group ? [] : (people.data ?? []).filter((p) => !ownIds.has(p.id)).sort(byName);
    const asOption = (p: ChatPerson, inChat: boolean): MentionOption => ({
      key: `USER:${p.id}`,
      label: `@${localized(p)}`,
      person: p,
      inChat,
      mention: { type: 'USER', id: p.id, fullName: p.fullName, fullNameAr: p.fullNameAr, isActive: true, me: false },
    });
    return [
      ...own.filter((p) => matches([p.fullName, p.fullNameAr])).map((p) => asOption(p, true)),
      // Everyone `/chat/people` lists is an employee still.
      ...rest
        .filter((p) => matches([p.fullName, p.fullNameAr]))
        .map((p) => asOption({ ...p, fullNameAr: p.fullNameAr ?? null, jobTitle: p.jobTitle ?? null, isActive: true }, false)),
    ].slice(0, 40);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger?.sign, trigger?.query, references.data, people.data, chat, locale]);

  // A new name being typed starts the list from its top.
  useEffect(() => setActive(0), [trigger?.sign, trigger?.start, trigger?.query]);

  /** The name picked from the list in place of what was typed for it. */
  const pick = (option: MentionOption) => {
    if (!trigger || caret === null) return;
    const placed = insertMention(text, mentions, trigger, caret, option.label, option.mention);
    update(placed.text, placed.mentions);
    setCaret(placed.caret);
    const el = area.current;
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(placed.caret, placed.caret);
    });
  };

  const onChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    const value = el.value;
    // Backspace into a mention takes all of it, as WhatsApp's box does.
    const whole = deleteWhole(mentions, text, value);
    if (whole) {
      update(whole.text, followEdit(mentions, text, whole.text));
      setCaret(whole.caret);
      requestAnimationFrame(() => el.setSelectionRange(whole.caret, whole.caret));
      return;
    }
    update(value, followEdit(mentions, text, value));
    setCaret(el.selectionStart === el.selectionEnd ? el.selectionStart : null);
  };

  const listShown = !!trigger && (options.length > 0 || trigger.sign === '#');

  // The box grows with what is typed, up to about six lines.
  useLayoutEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [text]);

  useEffect(() => {
    if (!panel) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (pickerBox.current?.contains(target) || area.current?.contains(target)) return;
      setPanel(null);
    };
    // Escape closes it wherever the focus is — on its own button too — and the box takes the focus back.
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      setPanel(null);
      area.current?.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [panel]);

  const send = () => {
    if (!canSend) return;
    const files = uploaded.map((i) => i.file!);
    // The words go without the blanks around them, so the mentions move back by those in front.
    const sent = trimWithMentions(text, mentions);
    enqueue(qc, id, {
      clientId: newClientId(),
      body: sent.words ? sent.words : null,
      createdAt: new Date().toISOString(),
      status: 'sending',
      fileIds: files.map((f) => f.id),
      attachments: files.map((file) => ({ id: file.id, commentId: null, createdAt: file.createdAt, file })),
      ...(sent.mentions.length ? { mentions: sent.mentions } : {}),
      ...answering(),
    });
    update('');
    uploads.clear();
    area.current?.focus();
  };

  const sendVoice = (blob: Blob, durationMs: number) =>
    enqueue(qc, id, {
      clientId: newClientId(),
      body: null,
      createdAt: new Date().toISOString(),
      status: 'sending',
      voice: { blob, url: URL.createObjectURL(blob), durationMs, name: voiceNoteName(blob.type) },
      ...answering(),
    });

  /** A sticker goes the moment it is picked, alone — as WhatsApp sends one. */
  const sendSticker = (sticker: ChatSticker) => {
    if (!ready) return;
    rememberSticker(sticker.id);
    setPanel(null);
    enqueue(qc, id, {
      clientId: newClientId(),
      body: null,
      createdAt: new Date().toISOString(),
      status: 'sending',
      sticker,
      ...answering(),
    });
    area.current?.focus();
  };

  const insertEmoji = (emoji: string) => {
    const el = area.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    const next = text.slice(0, start) + emoji + text.slice(end);
    update(next, followEdit(mentions, text, next));
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const caret = start + emoji.length;
      el.setSelectionRange(caret, caret);
    });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // An input method finishing a word (Arabic keyboards) presses Enter too.
    if (e.nativeEvent.isComposing) return;
    // The "@" or "#" list, while it is open: the arrows go through it, Enter or Tab picks, Escape closes it.
    if (trigger && options.length > 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const step = e.key === 'ArrowDown' ? 1 : -1;
        setActive((i) => (i + step + options.length) % options.length);
        return;
      }
      if ((e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) || (e.key === 'Tab' && !e.shiftKey)) {
        e.preventDefault();
        pick(options[Math.min(active, options.length - 1)]);
        return;
      }
    }
    if (trigger && e.key === 'Escape') {
      e.preventDefault();
      setDismissed(trigger.start);
      return;
    }
    if (e.key === 'Enter') {
      // Enter sends (Shift+Enter a new line) — or, by choice, Ctrl+Enter sends and Enter starts a line.
      const sends = enterSends ? !e.shiftKey : e.ctrlKey || e.metaKey;
      if (sends) {
        e.preventDefault();
        send();
      }
    } else if (e.key === 'Escape' && panel) {
      e.preventDefault();
      setPanel(null);
    } else if (e.key === 'Escape' && replyTo) {
      e.preventDefault();
      onCancelReply();
    }
  };

  // Two people's chat with someone who left takes nothing more; a group goes on with whoever is in it.
  if (chat?.other && !chat.other.isActive) {
    return (
      <div className="shrink-0 border-t hairline bg-[var(--surface-card)] px-4 py-3.5 text-center text-body-sm text-muted">
        {t('chat.cannotSend', { name: localized(chat.other) })}
      </div>
    );
  }

  // Whom the reply answers: "You", the other person — or, in a group, whoever wrote it.
  const answeredName = replyTo
    ? replyTo.mine
      ? t('chat.you')
      : isGroupChat(chat)
        ? replyTo.sender
          ? localized(replyTo.sender)
          : t('chat.group.someone')
        : localized(chat?.other)
    : '';

  return (
    <div className="relative shrink-0 border-t hairline bg-[var(--surface-card)]">
      {listShown && trigger && (
        <MentionList
          id={listId}
          sign={trigger.sign}
          query={trigger.query}
          options={options}
          active={Math.min(active, Math.max(0, options.length - 1))}
          loading={trigger.sign === '#' && (references.isFetching || refQuery !== trigger.query)}
          group={isGroupChat(chat)}
          onActive={setActive}
          onPick={pick}
        />
      )}
      {replyTo && (
        <div className="flex items-center gap-2 border-b hairline px-3 py-2">
          <div className="min-w-0 flex-1">
            <p className="mb-1 flex items-center gap-1.5 text-caption text-muted">
              <Reply className="h-3.5 w-3.5 flip-rtl" aria-hidden />
              <bdi>{t('chat.replyingTo', { name: answeredName })}</bdi>
            </p>
            <QuoteCard quote={quoteOf(replyTo)} name={answeredName} />
          </div>
          <IconButton label={t('chat.cancelReply')} onClick={onCancelReply}>
            <X className="h-5 w-5" aria-hidden />
          </IconButton>
        </div>
      )}
      {uploads.items.length > 0 && (
        <div className="max-h-56 overflow-y-auto border-b hairline px-3 py-2.5">
          <PendingTray uploads={uploads} />
        </div>
      )}
      <div className="flex items-end gap-1 px-2 py-2 sm:gap-1.5 sm:px-3">
        {recording ? (
          <VoiceRecorder
            maxSeconds={uploads.policy?.maxVoiceNoteSeconds ?? 600}
            doneLabel={t('chat.send')}
            doneIcon={<Send className="h-4 w-4 flip-rtl" />}
            onDone={(blob, ms) => {
              setRecording(false);
              sendVoice(blob, ms);
            }}
            onCancel={() => setRecording(false)}
            onError={(message) => {
              setRecording(false);
              toast.error(message);
            }}
          />
        ) : (
          <>
            <div ref={pickerBox} className="relative flex shrink-0">
              <IconButton
                label={t('chat.emoji')}
                pressed={panel === 'emoji'}
                keepFocus
                onClick={() => setPanel((v) => (v === 'emoji' ? null : 'emoji'))}
              >
                <Smile className="h-5 w-5" aria-hidden />
              </IconButton>
              {offersStickers && (
                <IconButton
                  label={t('chat.stickers')}
                  pressed={panel === 'stickers'}
                  keepFocus
                  onClick={() => setPanel((v) => (v === 'stickers' ? null : 'stickers'))}
                >
                  <Sticker className="h-5 w-5" aria-hidden />
                </IconButton>
              )}
              {panel === 'emoji' && (
                <EmojiPicker
                  onPick={insertEmoji}
                  className="absolute bottom-full start-0 z-dropdown mb-2 h-80 w-[min(22rem,calc(100vw-1.5rem))]"
                />
              )}
              {panel === 'stickers' && (
                <StickerPicker
                  packs={packs}
                  loading={tray.isPending}
                  failed={tray.isError}
                  manages={managesStickers}
                  onPick={sendSticker}
                  onClose={() => {
                    setPanel(null);
                    area.current?.focus();
                  }}
                  className="absolute bottom-full start-0 z-dropdown mb-2 h-80 w-[min(22rem,calc(100vw-1.5rem))]"
                />
              )}
            </div>
            <IconButton label={t('chat.attach')} onClick={() => fileInput.current?.click()}>
              <Paperclip className="h-5 w-5" aria-hidden />
            </IconButton>
            <input
              ref={fileInput}
              type="file"
              multiple
              hidden
              accept={uploads.policy?.extensions.join(',')}
              onChange={(e) => {
                if (e.target.files) uploads.addFiles(e.target.files);
                e.target.value = '';
              }}
            />
            <textarea
              ref={area}
              rows={1}
              value={text}
              // Only with words: on an empty box, `auto` lays an Arabic placeholder out left to right.
              dir={text ? 'auto' : undefined}
              placeholder={t('chat.placeholder')}
              aria-label={t('chat.placeholder')}
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={listShown}
              aria-controls={listShown ? `${listId}-list` : undefined}
              aria-activedescendant={listShown && options.length ? `${listId}-${Math.min(active, options.length - 1)}` : undefined}
              onChange={onChange}
              onSelect={(e) => {
                const el = e.currentTarget;
                setCaret(el.selectionStart === el.selectionEnd ? el.selectionStart : null);
              }}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              onKeyDown={onKeyDown}
              onPaste={uploads.onPaste}
              className="max-h-40 min-h-10 min-w-0 flex-1 resize-none rounded-lg border hairline bg-[var(--surface-sunken)] px-3 py-2 text-body text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:border-primary dark:placeholder:text-[var(--text-secondary)]"
            />
            {nothing ? (
              <IconButton label={t('chat.record')} onClick={() => setRecording(true)}>
                <Mic className="h-5 w-5" aria-hidden />
              </IconButton>
            ) : (
              <button
                type="button"
                onClick={send}
                disabled={!canSend}
                aria-label={t('chat.send')}
                title={uploads.busy ? t('attachments.waitForUploads') : enterSends ? t('chat.send') : t('chat.sendCtrl')}
                className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary text-white shadow-sm transition-colors duration-fast hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {uploads.busy ? (
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                ) : (
                  <Send className="h-5 w-5 flip-rtl" aria-hidden />
                )}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** A press that leaves the focus — and the cursor — in the message box. */
const keepFocus = (e: { preventDefault: () => void }) => e.preventDefault();

/** A name the "@" or "#" list offers: how it goes into the words, and what it names. */
interface MentionOption {
  key: string;
  label: string;
  mention: Omit<ChatMention, 'offset' | 'length'>;
  /** "@": the person — and, in two people's chat, whether it is the other of the two. */
  person?: ChatPerson;
  inChat?: boolean;
  /** "#": the task or the ticket. */
  reference?: ChatReference;
}

/**
 * The "@" or "#" list over the message box (operator, 2026-10-22: "use @ for
 * mention person and # to mention task or ticket"). "@": in a group, its
 * members alone ("in group chat when mention employee must one of group
 * employees"); in two people's chat, the other first, then everyone else. "#":
 * the tickets and tasks the writer can open, the latest first. The arrows go
 * through it and Enter or Tab picks, from the box, which keeps the focus; a
 * press picks too.
 */
function MentionList({
  id,
  sign,
  query,
  options,
  active,
  loading,
  group,
  onActive,
  onPick,
}: {
  id: string;
  sign: '@' | '#';
  query: string;
  options: MentionOption[];
  active: number;
  loading: boolean;
  group: boolean;
  onActive: (index: number) => void;
  onPick: (option: MentionOption) => void;
}) {
  const { t, locale, localized } = useI18n();
  const list = useRef<HTMLUListElement>(null);
  useEffect(() => {
    list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const firstOutside = options.findIndex((o) => o.person && !o.inChat);
  const heading = (label: string) => (
    <li role="presentation" className="px-3 pb-1 pt-2 text-overline uppercase text-[var(--text-tertiary)]">
      {label}
    </li>
  );
  return (
    <div className="absolute inset-x-2 bottom-full z-dropdown mb-1 overflow-hidden rounded-lg bg-[var(--surface-card)] shadow-lg ring-1 ring-black/10 dark:ring-white/10">
      <p className="flex items-center gap-1.5 border-b hairline px-3 py-2 text-caption font-semibold text-muted">
        {sign === '@' ? <AtSign className="h-3.5 w-3.5" aria-hidden /> : <Hash className="h-3.5 w-3.5" aria-hidden />}
        {sign === '@' ? t('chat.mention.people') : t('chat.mention.references')}
        {loading && <Loader2 className="ms-auto h-3.5 w-3.5 animate-spin" aria-label={t('chat.mention.searching')} />}
      </p>
      {options.length === 0 ? (
        <p className="px-3 py-3 text-body-sm text-muted">
          {loading ? t('chat.mention.searching') : query ? t('chat.mention.noMatch', { q: query }) : t('chat.mention.none')}
        </p>
      ) : (
        <ul ref={list} id={`${id}-list`} role="listbox" aria-label={sign === '@' ? t('chat.mention.people') : t('chat.mention.references')} className="max-h-64 overflow-y-auto py-1">
          {options.map((option, i) => (
            <Fragment key={option.key}>
              {/* A group lists its members alone; two people's chat, the other, then everyone else. */}
              {!group && option.person && i === 0 && option.inChat && heading(t('chat.mention.inChat'))}
              {!group && i === firstOutside && heading(t('chat.mention.others'))}
              <li
                id={`${id}-${i}`}
                role="option"
                aria-selected={i === active}
                onMouseDown={keepFocus}
                onMouseEnter={() => onActive(i)}
                onClick={() => onPick(option)}
                className={clsx(
                  'flex cursor-pointer items-center gap-3 px-3 py-2',
                  i === active ? 'bg-[var(--surface-sunken)]' : '',
                )}
              >
                {option.person ? (
                  <>
                    <ChatAvatar person={option.person} size="md" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-body font-medium text-[var(--text-primary)]">
                        <bdi>{localized(option.person)}</bdi>
                      </span>
                      {option.person.jobTitle && (
                        <span className="block truncate text-caption text-muted">
                          <bdi>{option.person.jobTitle}</bdi>
                        </span>
                      )}
                    </span>
                  </>
                ) : option.reference ? (
                  <>
                    <span
                      aria-hidden
                      className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-primary/12 text-primary-600 dark:text-primary-400"
                    >
                      {option.reference.type === 'TICKET' ? <Ticket className="h-5 w-5" /> : <ListTodo className="h-5 w-5" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-body font-medium text-[var(--text-primary)]">
                        <bdi>{option.reference.type === 'TICKET' ? option.label : option.reference.title}</bdi>
                      </span>
                      <span className="block truncate text-caption text-muted">
                        <bdi>
                          {[
                            option.reference.type === 'TICKET'
                              ? option.reference.title
                              : option.reference.leader
                                ? t('chat.mention.leaderTask')
                                : t('chat.mention.task'),
                            enumLabel(option.reference.status, locale),
                          ].join(' · ')}
                        </bdi>
                      </span>
                    </span>
                  </>
                ) : null}
              </li>
            </Fragment>
          ))}
        </ul>
      )}
      <p className="hidden border-t hairline px-3 py-1.5 text-caption text-faint sm:block">{t('chat.mention.keys')}</p>
    </div>
  );
}

/**
 * The chat's stickers (operator, 2026-10-20: "in chat system i want to add
 * stickers to chats"): a tab a pack — the administrators' packs, in their order
 * — and the ones sent last from this browser first. A press sends one at once.
 * Whoever manages the packs finds the way to them at its foot.
 */
function StickerPicker({
  packs,
  loading,
  failed,
  manages,
  onPick,
  onClose,
  className,
}: {
  packs: ChatStickerPack[];
  loading: boolean;
  failed: boolean;
  manages: boolean;
  onPick: (sticker: ChatSticker) => void;
  onClose: () => void;
  className?: string;
}) {
  const { t, localized } = useI18n();
  const offered = useMemo(() => new Map(packs.flatMap((p) => p.stickers.map((s) => [s.id, s] as const))), [packs]);
  // Only those still offered: one taken out of its pack since is not sent again from here.
  const recent = useMemo(
    () => recentStickers().flatMap((stickerId) => offered.get(stickerId) ?? []),
    [offered],
  );
  const [tab, setTab] = useState<string | null>(null);
  const current = tab ?? (recent.length ? 'recent' : (packs[0]?.id ?? 'recent'));
  const pack = packs.find((p) => p.id === current);
  const shown = current === 'recent' ? recent : (pack?.stickers ?? []);
  const title = current === 'recent' ? t('chat.stickers.recent') : pack ? localized(pack) : '';

  const tabs = [
    { id: 'recent', label: t('chat.stickers.recent'), icon: null as ChatSticker | null },
    ...packs.map((p) => ({ id: p.id, label: localized(p), icon: p.stickers[0] ?? null })),
  ];

  return (
    <div
      role="dialog"
      aria-label={t('chat.stickers')}
      onKeyDown={(e) => {
        if (e.key !== 'Escape') return;
        e.stopPropagation();
        onClose();
      }}
      className={clsx('flex flex-col overflow-hidden rounded-lg surface shadow-xl', className)}
    >
      {packs.length > 0 && (
        <div className="flex shrink-0 gap-0.5 overflow-x-auto overflow-y-hidden border-b hairline p-1" role="tablist">
          {tabs.map((x) => (
            <button
              key={x.id}
              type="button"
              role="tab"
              aria-selected={current === x.id}
              aria-label={x.label}
              title={x.label}
              onMouseDown={keepFocus}
              onClick={() => setTab(x.id)}
              className={clsx(
                'grid h-9 w-9 shrink-0 place-items-center rounded-sm transition-colors duration-fast',
                current === x.id
                  ? 'bg-primary/12 text-primary-600 dark:text-primary-400'
                  : 'text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]',
              )}
            >
              {x.icon ? (
                <img src={x.icon.url} alt="" className="h-7 w-7 object-contain" draggable={false} />
              ) : (
                <Clock className="h-4 w-4" aria-hidden />
              )}
            </button>
          ))}
        </div>
      )}
      {packs.length > 0 && (
        <p className="shrink-0 truncate px-3 pb-1 pt-2 text-overline uppercase text-[var(--text-tertiary)]">{title}</p>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        {loading ? (
          <div className="grid h-full place-items-center">
            <Spinner />
          </div>
        ) : failed && packs.length === 0 ? (
          <p className="px-2 py-8 text-center text-body-sm text-muted">{t('chat.stickers.failed')}</p>
        ) : packs.length === 0 ? (
          <div className="px-3 py-8 text-center">
            <Sticker className="mx-auto mb-2 h-8 w-8 text-[var(--text-tertiary)]" aria-hidden />
            <p className="text-body-sm text-muted">{t('chat.stickers.none')}</p>
          </div>
        ) : shown.length === 0 ? (
          <p className="px-2 py-8 text-center text-body-sm text-muted">{t('chat.stickers.noRecent')}</p>
        ) : (
          <div className="grid grid-cols-4 gap-1">
            {shown.map((sticker) => (
              <button
                key={sticker.id}
                type="button"
                onMouseDown={keepFocus}
                onClick={() => onPick(sticker)}
                aria-label={t('chat.stickers.send', { label: sticker.label })}
                title={sticker.label}
                className="grid aspect-square place-items-center rounded-md p-1 transition-[background-color,transform] duration-fast hover:scale-105 hover:bg-[var(--surface-sunken)] focus-visible:bg-[var(--surface-sunken)]"
              >
                <img src={sticker.url} alt="" loading="lazy" draggable={false} className="h-full w-full object-contain" />
              </button>
            ))}
          </div>
        )}
      </div>
      {manages && (
        <div className="shrink-0 border-t hairline px-3 py-2 text-end">
          <Link
            to="/sticker-packs"
            className="text-caption font-medium text-primary-600 hover:underline dark:text-primary-400"
          >
            {t('chat.stickers.manage')}
          </Link>
        </div>
      )}
    </div>
  );
}

/**
 * Forward: choose up to five chats — your groups, then people, your recent chats
 * first — and the message goes into each, marked forwarded: into your chat with
 * each person, and into each group. The page stays on the chat it came from.
 */
function ForwardDialog({ message, onClose }: { message: ChatMessage; onClose: () => void }) {
  const { t, locale, localized } = useI18n();
  const qc = useQueryClient();
  const toast = useToast();
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState<string[]>([]);
  const [chosenGroups, setChosenGroups] = useState<string[]>([]);
  const matches = useMatcher(query);
  const people = useQuery({
    queryKey: chatKeys.people,
    queryFn: () => api.get<ChatPersonOption[]>('/chat/people'),
    staleTime: 30_000,
  });
  const chats = qc.getQueryData<ChatList>(chatKeys.list)?.items ?? [];
  const recent = new Map(chats.flatMap((c, i) => (c.other ? [[c.other.id, i] as const] : [])));
  const groups = chats.filter((c) => isGroupChat(c) && matches([c.group?.name]));
  const shown = (people.data ?? [])
    .filter((p) => matches([p.fullName, p.fullNameAr, p.jobTitle, ...p.departments.flatMap((d) => [d.name, d.nameAr])]))
    .sort(
      (a, b) =>
        (recent.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (recent.get(b.id) ?? Number.MAX_SAFE_INTEGER) ||
        localized(a).localeCompare(localized(b), locale),
    );
  const total = chosen.length + chosenGroups.length;
  const full = total >= MAX_FORWARD_TO;

  const send = useMutation({
    mutationFn: () => forwardMessage(qc, message.id, chosen, chosenGroups),
    onSuccess: () => {
      toast.success(total === 1 ? t('chat.forwardDone') : t('chat.forwardDoneMany', { n: formatNumber(total, locale) }));
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggle = (id: string) =>
    setChosen((list) => (list.includes(id) ? list.filter((x) => x !== id) : full ? list : [...list, id]));
  const toggleGroup = (id: string) =>
    setChosenGroups((list) => (list.includes(id) ? list.filter((x) => x !== id) : full ? list : [...list, id]));

  return (
    <Modal
      open
      onClose={() => {
        if (!send.isPending) onClose();
      }}
      title={t('chat.forwardTitle')}
      size="sm"
      footer={
        <>
          <Button onClick={onClose} disabled={send.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon={<Forward className="h-4 w-4 flip-rtl" />}
            disabled={total === 0}
            loading={send.isPending}
            onClick={() => send.mutate()}
          >
            {t('chat.forward')}
          </Button>
        </>
      }
    >
      <p className="mb-3 text-body-sm text-muted">{t('chat.forwardHint', { n: MAX_FORWARD_TO })}</p>
      <SearchBox value={query} onChange={setQuery} placeholder={t('chat.search')} autoFocus />
      <div className="mt-2 max-h-80 overflow-y-auto">
        {groups.length > 0 && (
          <>
            <p className="px-2 pb-1 pt-2 text-overline uppercase text-[var(--text-tertiary)]">{t('chat.forwardGroups')}</p>
            <ul>
              {groups.map((g) => {
                const picked = chosenGroups.includes(g.id);
                return (
                  <li key={g.id}>
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={picked}
                      disabled={!picked && full}
                      onClick={() => toggleGroup(g.id)}
                      className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-start transition-colors duration-fast hover:bg-[var(--surface-sunken)] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <span
                        aria-hidden
                        className={clsx(
                          'grid h-5 w-5 shrink-0 place-items-center rounded-full border',
                          picked ? 'border-primary bg-primary text-white' : 'hairline',
                        )}
                      >
                        {picked && <Check className="h-3.5 w-3.5" />}
                      </span>
                      <GroupAvatar size="md" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-body-sm font-medium text-[var(--text-primary)]">
                          <bdi>{g.group?.name}</bdi>
                        </span>
                        <span className="block truncate text-caption text-muted">
                          {g.group?.members === 1
                            ? t('chat.group.memberOne')
                            : t('chat.group.membersCount', { n: formatNumber(g.group?.members ?? 0, locale) })}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            <p className="px-2 pb-1 pt-3 text-overline uppercase text-[var(--text-tertiary)]">{t('chat.forwardPeople')}</p>
          </>
        )}
        {people.isLoading ? (
          <LoadingBlock label={t('chat.peopleLoading')} />
        ) : shown.length === 0 ? (
          <p className="px-2 py-8 text-center text-body-sm text-muted">{t('chat.noPeople')}</p>
        ) : (
          <ul>
            {shown.map((person) => {
              const picked = chosen.includes(person.id);
              return (
                <li key={person.id}>
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={picked}
                    disabled={!picked && full}
                    onClick={() => toggle(person.id)}
                    className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-start transition-colors duration-fast hover:bg-[var(--surface-sunken)] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <span
                      aria-hidden
                      className={clsx(
                        'grid h-5 w-5 shrink-0 place-items-center rounded-full border',
                        picked ? 'border-primary bg-primary text-white' : 'hairline',
                      )}
                    >
                      {picked && <Check className="h-3.5 w-3.5" />}
                    </span>
                    <ChatAvatar person={person} size="md" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-body-sm font-medium text-[var(--text-primary)]">
                        <bdi>{localized(person)}</bdi>
                      </span>
                      {person.jobTitle && (
                        <span className="block truncate text-caption text-muted">
                          <bdi>{person.jobTitle}</bdi>
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <p className="mt-2 text-caption text-muted tabular" aria-live="polite">
        {full ? t('chat.forwardMax', { n: MAX_FORWARD_TO }) : total > 0 ? t('chat.forwardChosen', { n: total }) : ''}
      </p>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// A group's info
// ---------------------------------------------------------------------------

/**
 * A group's info, as WhatsApp Web's: its name — which its admins change — who is
 * in it and who is invited, waiting to answer. Anyone in it invites people; its
 * admins take people out and make others admins; whoever sent an invitation, or
 * an admin, takes it back; anyone leaves. Beside the chat on a wide window, over
 * it on a narrower one.
 */
function GroupInfo({ chat, onClose }: { chat: ChatDetail; onClose: () => void }) {
  const { t, locale, localized } = useI18n();
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const groupName = chat.group?.name ?? '';
  const admin = chat.group?.admin ?? false;
  const members = [...(chat.members ?? [])].sort(
    (a, b) => Number(b.me) - Number(a.me) || Number(b.admin) - Number(a.admin) || localized(a).localeCompare(localized(b), locale),
  );
  const invitations = chat.invitations ?? [];
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(groupName);
  const [inviting, setInviting] = useState(false);
  const [removing, setRemoving] = useState<ChatGroupMember | null>(null);
  const [leaving, setLeaving] = useState(false);
  const closeButton = useRef<HTMLButtonElement>(null);
  const dialogOpen = inviting || !!removing || leaving;

  useEffect(() => {
    closeButton.current?.focus({ preventScroll: true });
  }, []);

  /** The group as the change left it: shown at once, and everything about it read again. */
  const changed = (next: ChatDetail) => {
    qc.setQueryData(chatKeys.one(chat.id), next);
    refreshGroup(qc, chat.id);
  };
  const failed = (e: Error) => {
    toast.error(e.message);
    refreshGroup(qc, chat.id);
  };

  const rename = useMutation({
    mutationFn: (next: string) => api.patch<ChatDetail>(`/chat/groups/${chat.id}`, { name: next }),
    onSuccess: (next) => {
      changed(next);
      setRenaming(false);
      toast.success(t('chat.group.renamed'));
    },
    onError: failed,
  });
  const remove = useMutation({
    mutationFn: (member: ChatGroupMember) => api.del<ChatDetail>(`/chat/groups/${chat.id}/members/${member.id}`),
    onSuccess: (next, member) => {
      changed(next);
      setRemoving(null);
      toast.success(t('chat.group.removed', { name: localized(member) }));
    },
    onError: failed,
  });
  const setAdmin = useMutation({
    mutationFn: ({ member, admin: make }: { member: ChatGroupMember; admin: boolean }) =>
      make
        ? api.put<ChatDetail>(`/chat/groups/${chat.id}/admins/${member.id}`)
        : api.del<ChatDetail>(`/chat/groups/${chat.id}/admins/${member.id}`),
    onSuccess: (next, { member, admin: made }) => {
      changed(next);
      toast.success(
        made ? t('chat.group.madeAdmin', { name: localized(member) }) : t('chat.group.dismissedAdmin', { name: localized(member) }),
      );
    },
    onError: failed,
  });
  const takeBack = useMutation({
    mutationFn: (invitation: ChatGroupInvitee) => api.del<ChatDetail>(`/chat/groups/${chat.id}/invitations/${invitation.id}`),
    onSuccess: (next) => {
      changed(next);
      toast.success(t('chat.group.takenBack'));
    },
    onError: failed,
  });
  const leave = useMutation({
    mutationFn: () => api.post(`/chat/groups/${chat.id}/leave`),
    onSuccess: () => {
      qc.removeQueries({ queryKey: chatKeys.one(chat.id) });
      qc.removeQueries({ queryKey: chatKeys.thread(chat.id) });
      refreshGroup(qc);
      toast.success(t('chat.group.left'));
      navigate('/chat');
    },
    onError: failed,
  });
  const openChat = useOpenChat();

  const words = name.trim();
  const length = Array.from(words).length;
  const canRename = words.length > 0 && length <= MAX_GROUP_NAME && words !== groupName && !rename.isPending;
  const starter = chat.group?.createdById ? members.find((m) => m.id === chat.group?.createdById) : undefined;
  const created = formatDate(chat.createdAt, locale);
  const alone = members.length === 1;

  return (
    <aside
      aria-label={t('chat.group.info')}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !dialogOpen && !renaming) onClose();
      }}
      // Beside the chat once the window leaves the chat a readable column (the sidebar, the chats and this
      // take ~1,000 px); over it on anything narrower.
      className="absolute inset-0 z-[3] flex min-h-0 flex-col bg-[var(--surface-card)] min-[1440px]:static min-[1440px]:z-auto min-[1440px]:w-[360px] min-[1440px]:shrink-0 min-[1440px]:border-s min-[1440px]:hairline"
    >
      <header className="flex h-16 shrink-0 items-center gap-2 border-b hairline px-2 sm:px-3">
        <button
          ref={closeButton}
          type="button"
          onClick={onClose}
          aria-label={t('chat.group.closeInfo')}
          title={t('chat.group.closeInfo')}
          className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-[var(--text-secondary)] transition-colors duration-fast hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>
        <h2 className="min-w-0 flex-1 truncate text-h3 text-[var(--text-primary)]">{t('chat.group.info')}</h2>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto bg-[var(--surface-sunken)]">
        <section className="bg-[var(--surface-card)] px-4 pb-5 pt-6 text-center">
          <div className="flex justify-center">
            <GroupAvatar size="xl" />
          </div>
          {renaming ? (
            <form
              className="mx-auto mt-4 max-w-xs text-start"
              onSubmit={(e) => {
                e.preventDefault();
                if (canRename) rename.mutate(words);
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Escape') return;
                e.stopPropagation();
                setRenaming(false);
                setName(groupName);
              }}
            >
              <label className="block">
                <span className="sr-only">{t('chat.newGroup.name')}</span>
                <input
                  value={name}
                  autoFocus
                  dir={name ? 'auto' : undefined}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('chat.newGroup.namePlaceholder')}
                  className="h-10 w-full rounded-md border hairline bg-[var(--surface-sunken)] px-3 text-body text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:border-primary dark:placeholder:text-[var(--text-secondary)]"
                />
              </label>
              <p
                className={clsx(
                  'mt-1 text-end text-caption tabular',
                  length > MAX_GROUP_NAME ? 'text-error-fg dark:text-error-dark' : 'text-faint',
                )}
              >
                {formatNumber(length, locale)}/{formatNumber(MAX_GROUP_NAME, locale)}
              </p>
              <div className="mt-2 flex justify-end gap-2">
                <Button
                  size="sm"
                  onClick={() => {
                    setRenaming(false);
                    setName(groupName);
                  }}
                  disabled={rename.isPending}
                >
                  {t('common.cancel')}
                </Button>
                <Button size="sm" type="submit" variant="primary" loading={rename.isPending} disabled={!canRename}>
                  {t('common.save')}
                </Button>
              </div>
            </form>
          ) : (
            <div className="mt-4 flex items-center justify-center gap-1">
              <h3 className="min-w-0 break-words text-h2 text-[var(--text-primary)]">
                <bdi>{groupName}</bdi>
              </h3>
              {admin && (
                <IconButton
                  label={t('chat.group.rename')}
                  onClick={() => {
                    setName(groupName);
                    setRenaming(true);
                  }}
                >
                  <Pencil className="h-4 w-4" aria-hidden />
                </IconButton>
              )}
            </div>
          )}
          <p className="mt-1 text-body-sm text-muted">
            {t('chat.group.kind')}
            <span aria-hidden> · </span>
            {members.length === 1
              ? t('chat.group.memberOne')
              : t('chat.group.membersCount', { n: formatNumber(members.length, locale) })}
          </p>
          <p className="mt-1 text-caption text-faint">
            <bdi>
              {starter?.me
                ? t('chat.group.createdByYou', { date: created })
                : starter
                  ? t('chat.group.createdBy', { name: localized(starter), date: created })
                  : t('chat.group.createdOn', { date: created })}
            </bdi>
          </p>
        </section>

        <section className="mt-2 bg-[var(--surface-card)] py-2" aria-label={t('chat.group.members')}>
          <p className="px-4 pb-1 pt-2 text-overline uppercase text-[var(--text-tertiary)]">
            {t('chat.group.members')} <span className="tabular">({formatNumber(members.length, locale)})</span>
          </p>
          <button
            type="button"
            onClick={() => setInviting(true)}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-start transition-colors duration-fast hover:bg-[var(--surface-sunken)]"
          >
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary text-white" aria-hidden>
              <UserPlus className="h-5 w-5" />
            </span>
            <span className="text-body font-medium text-[var(--text-primary)]">{t('chat.group.invite')}</span>
          </button>
          <ul>
            {members.map((member) => (
              <GroupMemberRow
                key={member.id}
                member={member}
                manage={admin && !member.me}
                busy={
                  (remove.isPending && remove.variables?.id === member.id) ||
                  (setAdmin.isPending && setAdmin.variables?.member.id === member.id)
                }
                onMessage={() => openChat.mutate(member.id)}
                onAdmin={(make) => setAdmin.mutate({ member, admin: make })}
                onRemove={() => setRemoving(member)}
              />
            ))}
          </ul>
          <p className="px-4 pb-2 pt-1 text-caption text-faint">{t('chat.group.adminNote')}</p>
        </section>

        {invitations.length > 0 && (
          <section className="mt-2 bg-[var(--surface-card)] py-2" aria-label={t('chat.group.invited')}>
            <p className="px-4 pb-1 pt-2 text-overline uppercase text-[var(--text-tertiary)]">
              {t('chat.group.invited')} <span className="tabular">({formatNumber(invitations.length, locale)})</span>
            </p>
            <ul>
              {invitations.map((invitation) => (
                <li key={invitation.id} className="flex items-center gap-3 px-4 py-2">
                  <ChatAvatar person={invitation.user} size="md" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-body-sm font-medium text-[var(--text-primary)]">
                      <bdi>{localized(invitation.user)}</bdi>
                    </span>
                    <span className="block truncate text-caption text-muted">
                      <bdi>
                        {invitation.invitedBy.id === user?.id
                          ? t('chat.group.invitedByYou', { when: listTime(invitation.createdAt, locale, t) })
                          : t('chat.group.invitedBy', {
                              name: localized(invitation.invitedBy),
                              when: listTime(invitation.createdAt, locale, t),
                            })}
                      </bdi>
                    </span>
                  </span>
                  {invitation.canCancel && (
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={takeBack.isPending && takeBack.variables?.id === invitation.id}
                      disabled={takeBack.isPending}
                      onClick={() => takeBack.mutate(invitation)}
                      aria-label={t('chat.group.takeBackLabel', { name: localized(invitation.user) })}
                    >
                      {t('chat.group.takeBack')}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="mt-2 bg-[var(--surface-card)] py-2">
          <button
            type="button"
            onClick={() => setLeaving(true)}
            className="flex w-full items-center gap-3 px-4 py-3 text-start text-body font-medium text-error-fg transition-colors duration-fast hover:bg-[var(--surface-sunken)] dark:text-error-dark"
          >
            <LogOut className="h-5 w-5 shrink-0 flip-rtl" aria-hidden />
            {t('chat.group.leave')}
          </button>
        </section>
      </div>

      {inviting && <InviteDialog chat={chat} onClose={() => setInviting(false)} onInvited={changed} />}
      {removing && (
        <ConfirmDialog
          title={<bdi>{t('chat.group.removeConfirm', { name: localized(removing), group: groupName })}</bdi>}
          confirmLabel={t('chat.group.remove')}
          danger
          busy={remove.isPending}
          onConfirm={() => remove.mutate(removing)}
          onCancel={() => setRemoving(null)}
        >
          <p>{t('chat.group.removeBody')}</p>
        </ConfirmDialog>
      )}
      {leaving && (
        <ConfirmDialog
          title={<bdi>{t('chat.group.leaveConfirm', { name: groupName })}</bdi>}
          confirmLabel={t('chat.group.leave')}
          danger
          busy={leave.isPending}
          onConfirm={() => leave.mutate()}
          onCancel={() => setLeaving(false)}
        >
          <p>{alone ? t('chat.group.leaveLast') : t('chat.group.leaveBody')}</p>
        </ConfirmDialog>
      )}
    </aside>
  );
}

/**
 * One of a group's members: "You" first, an admin marked, who is online. Its
 * menu messages them, and — for an admin — makes them an admin, or no longer,
 * and takes them out.
 */
function GroupMemberRow({
  member,
  manage,
  busy,
  onMessage,
  onAdmin,
  onRemove,
}: {
  member: ChatGroupMember;
  /** The caller is an admin, and this is someone else. */
  manage: boolean;
  busy: boolean;
  onMessage: () => void;
  onAdmin: (admin: boolean) => void;
  onRemove: () => void;
}) {
  const { t, localized } = useI18n();
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const name = member.me ? t('chat.you') : localized(member);
  const canMessage = !member.me && member.isActive;
  const pick = (action: () => void) => {
    setOpen(false);
    action();
  };
  const item =
    'flex w-full items-center gap-2.5 px-3 py-2 text-start text-body-sm text-[var(--text-primary)] transition-colors duration-fast hover:bg-[var(--surface-sunken)]';
  return (
    <li className="flex items-center gap-3 px-4 py-2">
      <ChatAvatar person={member} size="md" />
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-2">
          <span className="min-w-0 truncate text-body-sm font-medium text-[var(--text-primary)]">
            <bdi>{name}</bdi>
          </span>
          {member.admin && (
            <Badge tone="brand" className="shrink-0">
              {t('chat.group.admin')}
            </Badge>
          )}
        </span>
        <span className="block truncate text-caption text-muted">
          {member.isActive ? (
            member.online ? (
              <span className="font-medium text-success-fg dark:text-success">{t('chat.online')}</span>
            ) : (
              <bdi>{member.jobTitle ?? ''}</bdi>
            )
          ) : (
            t('chat.group.noLongerEmployee')
          )}
        </span>
      </span>
      {busy ? (
        <Spinner className="h-4 w-4" />
      ) : (
        (canMessage || manage) && (
          <>
            <button
              ref={button}
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={open}
              aria-label={t('chat.group.memberMenu', { name })}
              title={t('chat.group.memberMenu', { name })}
              className={clsx(
                'grid h-8 w-8 shrink-0 place-items-center rounded-full text-[var(--text-secondary)] transition-colors duration-fast hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]',
                open && 'bg-[var(--surface-sunken)] text-[var(--text-primary)]',
              )}
            >
              <MoreVertical className="h-4 w-4" aria-hidden />
            </button>
            {open && button.current && (
              <Floating anchor={button.current} onClose={() => setOpen(false)} prefer="below" align="end">
                <div
                  role="menu"
                  aria-label={t('chat.group.memberMenu', { name })}
                  className="w-60 overflow-hidden rounded-md surface py-1 shadow-lg"
                >
                  {canMessage && (
                    <button type="button" role="menuitem" data-autofocus="" onClick={() => pick(onMessage)} className={item}>
                      <MessageCircle className="h-4 w-4 shrink-0" aria-hidden />
                      <bdi className="min-w-0 truncate">{t('chat.group.message', { name })}</bdi>
                    </button>
                  )}
                  {manage &&
                    (member.admin ? (
                      <button type="button" role="menuitem" onClick={() => pick(() => onAdmin(false))} className={item}>
                        <ShieldOff className="h-4 w-4 shrink-0" aria-hidden />
                        {t('chat.group.dismissAdmin')}
                      </button>
                    ) : (
                      member.isActive && (
                        <button type="button" role="menuitem" onClick={() => pick(() => onAdmin(true))} className={item}>
                          <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden />
                          {t('chat.group.makeAdmin')}
                        </button>
                      )
                    ))}
                  {manage && (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => pick(onRemove)}
                      className={clsx(item, 'text-error-fg dark:text-error-dark')}
                    >
                      <UserMinus className="h-4 w-4 shrink-0" aria-hidden />
                      {t('chat.group.remove')}
                    </button>
                  )}
                </div>
              </Floating>
            )}
          </>
        )
      )}
    </li>
  );
}

/**
 * Invite people to a group — anyone in it does: every active employee not in it
 * nor invited already. Each gets an invitation in their bell and on top of their
 * chats, and joins only by accepting; nobody is put in a group.
 */
function InviteDialog({
  chat,
  onClose,
  onInvited,
}: {
  chat: ChatDetail;
  onClose: () => void;
  onInvited: (next: ChatDetail) => void;
}) {
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState<string[]>([]);
  const matches = useMatcher(query);
  const people = useQuery({
    queryKey: chatKeys.people,
    queryFn: () => api.get<ChatPersonOption[]>('/chat/people'),
    staleTime: 30_000,
  });
  const taken = new Set([...(chat.members ?? []).map((m) => m.id), ...(chat.invitations ?? []).map((i) => i.user.id)]);
  const free = (people.data ?? []).filter((p) => !taken.has(p.id));
  const shown = free
    .filter((p) => matches([p.fullName, p.fullNameAr, p.jobTitle, ...p.departments.flatMap((d) => [d.name, d.nameAr])]))
    .sort((a, b) => localized(a).localeCompare(localized(b), locale));

  const send = useMutation({
    mutationFn: () => api.post<ChatDetail>(`/chat/groups/${chat.id}/invitations`, { userIds: chosen }),
    onSuccess: (next) => {
      onInvited(next);
      toast.success(t('chat.inviteDone', { n: formatNumber(chosen.length, locale) }));
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const toggle = (id: string) => setChosen((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));

  return (
    <Modal
      open
      onClose={() => {
        if (!send.isPending) onClose();
      }}
      title={<bdi>{t('chat.inviteTitle', { name: chat.group?.name ?? '' })}</bdi>}
      size="sm"
      footer={
        <>
          <Button onClick={onClose} disabled={send.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon={<UserPlus className="h-4 w-4" />}
            disabled={chosen.length === 0}
            loading={send.isPending}
            onClick={() => send.mutate()}
          >
            {t('chat.inviteSend')}
          </Button>
        </>
      }
    >
      <p className="mb-3 text-body-sm text-muted">{t('chat.inviteHint')}</p>
      <SearchBox value={query} onChange={setQuery} placeholder={t('chat.searchPeople')} autoFocus />
      <div className="-mx-3 mt-2 max-h-80 overflow-y-auto">
        {people.isLoading ? (
          <LoadingBlock label={t('chat.peopleLoading')} />
        ) : free.length === 0 ? (
          <p className="px-2 py-8 text-center text-body-sm text-muted">{t('chat.inviteNobody')}</p>
        ) : shown.length === 0 ? (
          <p className="px-2 py-8 text-center text-body-sm text-muted">{t('chat.noPeople')}</p>
        ) : (
          <ul>
            {shown.map((person) => (
              <TickRow
                key={person.id}
                person={person}
                picked={chosen.includes(person.id)}
                onToggle={() => toggle(person.id)}
                note={[person.jobTitle, ...person.departments.map((d) => localized(d))].filter(Boolean).join(' · ')}
              />
            ))}
          </ul>
        )}
      </div>
      <p className="mt-2 text-caption text-muted tabular" aria-live="polite">
        {chosen.length > 0 ? t('chat.newGroup.chosen', { n: formatNumber(chosen.length, locale) }) : ''}
      </p>
    </Modal>
  );
}
